<template>
  <div class="row bg-white">
    <div class="col-12">
      <div class="row gy-2">

        <div class="col-12">
          <!-- Filter bar -->
          <div class="dash-filterbar">
            <NSelect
              v-model:value="FILTERS.ELEMENT"
              @update:value="onElementChange"
              :disabled="isLoading"
              :options="ELEMENT_OPTS"
              :theme-overrides="inputTheme"
              filterable clearable
              placeholder="Select commodity to view"
              :input-props="{ autocomplete: Math.random() }"
              class="dash-filterbar__select"
              size="small"
            />
            <NSelect
              v-model:value="FILTERS.PRODUCT"
              :disabled="isLoading || !HS_CODE_OPTS.length"
              :loading="hsLoading"
              :options="HS_CODE_OPTS"
              :theme-overrides="inputTheme"
              filterable clearable
              placeholder="Select HS Code"
              :input-props="{ autocomplete: Math.random() }"
              class="dash-filterbar__select dash-filterbar__select--sm"
              size="small"
              @clear="clearChart"
            />
            <div class="dash-filterbar__date">
              <span class="dash-filterbar__label">Select Year</span>
              <NDatePicker
                v-model:value="searchYear"
                type="year"
                :is-date-disabled="ts => isYearOutOfRange(ts) || (compareMode && sameYear(ts, compareYear))"
                :theme-overrides="inputTheme"
                :input-props="{ autocomplete: randomId }"
                class="dash-filterbar__datepicker"
                size="small"
              />
            </div>
            <div class="dash-filterbar__date">
              <button :class="['tf-compare-toggle', { active: compareMode }]" :disabled="isLoading"
                @click="toggleCompareMode" title="Compare two years">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M1.5 4h9M8 1.5L10.5 4 8 6.5M11.5 9h-9M5 6.5L2.5 9 5 11.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
                Compare
              </button>
              <template v-if="compareMode">
                <span class="dash-filterbar__label">vs</span>
                <NDatePicker
                  v-model:value="compareYear"
                  type="year"
                  :is-date-disabled="ts => isYearOutOfRange(ts) || sameYear(ts, searchYear)"
                  :theme-overrides="inputTheme"
                  :input-props="{ autocomplete: randomId }"
                  class="dash-filterbar__datepicker"
                  size="small"
                />
              </template>
            </div>
            <NButton
              size="small"
              :disabled="!FILTERS.ELEMENT || !FILTERS.PRODUCT || isLoading || (compareMode && sameYear(searchYear, compareYear))"
              @click="fetchChord()"
              color="#213ce5"
              style="background: linear-gradient(90deg, #213ce5, #2380f2); border: 1px solid transparent; flex-shrink: 0;"
              @mouseenter="e => e.currentTarget.style.border = '1px solid #00aeef'"
              @mouseleave="e => e.currentTarget.style.border = '1px solid transparent'"
            >
              Search
            </NButton>
          </div>

          <!-- Action row -->
          <div class="dash-actions">
            <div v-if="hasData" class="relative inline-block">
              <button class="dash-action-btn" @click="chartMenuOpen = !chartMenuOpen">
                <img :src="DDICONS.download" alt="" class="dash-action-btn__icon" />
                Export Graphic
              </button>
              <div v-show="chartMenuOpen" class="split-menu" role="menu">
                <button role="menuitem" @click="exportChartAs('png'); chartMenuOpen = false;">Export as PNG</button>
                <button role="menuitem" @click="exportChartAs('svg'); chartMenuOpen = false;">Export as SVG</button>
              </div>
            </div>
            <span class="dash-actions__disclaimer" @click="showDisclaimer = true"><em>Disclaimer</em></span>
          </div>
        </div>

        <!-- Chart area -->
        <div class="col-12">
          <!-- Toolbar: Filters / Reset -->
          <div v-if="hasSearched" class="tf-toolbar">
            <div class="tf-view-toggle">
              <button :class="['tf-view-btn', { active: chartView === 'chord' }]" @click="setChartView('chord')">
                <img :src="DDICONS.chord" alt="" class="dash-action-btn__icon" />
                Chord
              </button>
              <button :class="['tf-view-btn', { active: chartView === 'sankeynormal' }]" @click="setChartView('sankeynormal')">
                <img :src="DDICONS.sankey" alt="" class="dash-action-btn__icon" />
                Sankey
              </button>
              <button :class="['tf-view-btn', { active: chartView === 'map' }]" @click="setChartView('map')">
                <img :src="DDICONS.map" alt="" class="dash-action-btn__icon" />
                Map
              </button>
            </div>
            <div class="tf-toolbar-centre">
              <button :class="['tf-rail-btn tf-rail-btn--inline', { active: activeFilter === 'regionFilter' || effectiveFilter.length }]"
                @click="toggleFilter('regionFilter')" title="Filter countries or regions">
                <img :src="DDICONS.filter" alt="" class="dash-action-btn__icon" />
                <span>Filters</span>
                <span v-if="effectiveFilter.length" class="tf-rail-badge tf-rail-badge--inline"><span class="tf-rail-badge__num">{{ effectiveFilter.length }}</span></span>
              </button>
              <button class="tf-rail-btn tf-rail-btn--inline tf-rail-btn--reset" @click="resetFilters" title="Reset all filters">
                <img :src="DDICONS.reset" alt="" class="dash-action-btn__icon" />
                <span>Reset</span>
              </button>
            </div>
            <!-- Year comparison: either year on its own, or the change between them -->
            <!-- Comparing: one chart coloured by the change (hover for both
                 years), or both years' charts side by side (chord/Sankey only -
                 the map always shows the change). -->
            <div v-if="isComparing && chartView !== 'map'" class="tf-year-switch" role="tablist">
              <span class="tf-year-switch__glider" :style="{ transform: `translateX(${yearView === 'both' ? 100 : 0}%)` }"></span>
              <button :class="['tf-year-switch__btn', { active: yearView === 'change' }]" @click="setYearView('change')">
                <img :src="DDICONS.change" alt="" class="dash-action-btn__icon" /> Change
              </button>
              <button :class="['tf-year-switch__btn', { active: yearView === 'both' }]" @click="setYearView('both')">
                <img :src="DDICONS.sideBySide" alt="" class="dash-action-btn__icon" />
                Side by side
              </button>
            </div>
          </div>

          <div class="chord-layout">
            <!-- What gets exported: the chart plus the export-only source stamp -->
            <div ref="exportDiv" class="chord-export-area">
              <!-- Chord diagram -->
              <div ref="captureDiv" :class="['chord-canvas-wrap', { 'chord-canvas-wrap--split': showSplit }]">
                <p v-if="!hasSearched && !isLoading" class="chord-empty">
                  Select a commodity and HS code, then click Search to view the trade flow.
                </p>
                <p v-if="hasSearched && !hasData && !isLoading" class="chord-empty">
                  No trade flows match the current filters. Try clearing the region/country filters.
                </p>
                <div v-if="isLoading" class="chord-empty">Loading…</div>
                <!-- Side by side: earlier year on the left (svgBaseRef), later year on the right (svgRef) -->
                <svg v-show="showSplit" ref="svgBaseRef" class="chord-svg chord-svg--base"></svg>
                <svg ref="svgRef" class="chord-svg"></svg>
                <template v-if="showSplit && splitTotals">
                  <div v-for="side in splitHeads" :key="side.year" :class="['tf-split-head', `tf-split-head--${side.key}`]">
                    <span class="tf-split-head__year">{{ side.year }}</span>
                    <span class="tf-split-head__total">
                      {{ fmtCompact(side.total) }}{{ splitTotals.units ? ' ' + splitTotals.units : '' }} traded
                    </span>
                    <span v-if="side.key === 'curr'" class="tf-change-pill"
                      :style="{ background: changeColor(splitTotals.prev, splitTotals.curr) }">
                      {{ pctLabel(splitTotals.prev, splitTotals.curr) }}
                    </span>
                  </div>
                </template>

                <!-- Map arc thickness - left out of exported graphics -->
                <div v-if="chartView === 'map' && hasData && !isLoading" class="tf-arc-width tf-no-export">
                  <Transition name="tf-arc-width-slide">
                    <label v-if="arcWidthOpen" class="tf-arc-width__panel">
                      <span>Flow thickness</span>
                      <input type="range" min="0.25" max="4" step="0.25" :value="mapArcScale"
                        @input="onMapArcScale($event.target.valueAsNumber)" />
                      <span class="tf-arc-width__value">{{ mapArcScale }}×</span>
                    </label>
                  </Transition>
                  <button :class="['tf-arc-width__btn', { active: arcWidthOpen }]" title="Flow thickness"
                    @click="arcWidthOpen = !arcWidthOpen">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 3.5h12" stroke="currentColor" stroke-width="1" stroke-linecap="round"/><path d="M2 7.5h12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M2 12h12" stroke="currentColor" stroke-width="3.2" stroke-linecap="round"/></svg>
                  </button>
                </div>

                <!-- Tooltip -->
                <div ref="tooltipEl" class="chord-tooltip" style="display:none;"></div>

              </div>
              <!-- Change legend + headline totals - inside exportDiv so exports explain the colours -->
              <div v-if="compareSummary && hasData" class="tf-change-legend">
                <div class="tf-change-legend__scale">
                  <span class="tf-change-legend__title">Change {{ compareSummary.baseYear }} → {{ compareSummary.year }}</span>
                  <div class="tf-change-legend__bar-wrap">
                    <div class="tf-change-legend__bar" :style="{ background: CHANGE_GRADIENT_CSS }"></div>
                    <div class="tf-change-legend__ticks">
                      <span v-for="t in CHANGE_TICKS" :key="t.label" :style="{ left: `${t.pos * 100}%` }">{{ t.label }}</span>
                    </div>
                  </div>
                </div>
                <div class="tf-change-legend__total">
                  <span class="tf-change-legend__muted">Total traded</span>
                  {{ fmtCompact(compareSummary.totals.prev) }}
                  <span class="tf-change-legend__arrow">→</span>
                  <strong>{{ fmtCompact(compareSummary.totals.curr) }}</strong>{{ compareSummary.units ? ' ' + compareSummary.units : '' }}
                  <span class="tf-change-pill" :style="{ background: changeColor(compareSummary.totals.prev, compareSummary.totals.curr) }">
                    {{ pctLabel(compareSummary.totals.prev, compareSummary.totals.curr) }}
                  </span>
                </div>
              </div>
              <!-- Source/date stamp - inside exportDiv so it's in exported graphics too -->
              <div class="chord-export-source">
                <p style="font-size: smaller;"><em>Source: Project Blue</em></p>
                <p style="font-size: smaller; margin-top: -15px;"><em>Accessed: {{ updatedDate }}</em></p>
              </div>
            </div>

            <!-- Biggest movers between the two years. Hovering a row spotlights
                 that flow on the chord/Sankey (the map has no per-flow hook). -->
            <aside v-if="showMovers" class="tf-movers modern-scrollbar">
              <template v-if="compareSummary && hasData">
                <div class="tf-movers__head">
                  <span class="tf-panel-title">Biggest movers</span>
                  <span class="tf-movers__years">{{ compareSummary.baseYear }} → {{ compareSummary.year }}</span>
                </div>
                <div v-for="group in moverGroups" :key="group.key">
                  <div class="tf-movers__group" :style="{ color: group.color }">
                    <svg width="9" height="9" viewBox="0 0 9 9"><path :d="group.key === 'gainers' ? 'M4.5 1L8 7H1z' : 'M4.5 8L8 2H1z'" fill="currentColor"/></svg>
                    {{ group.label }}
                  </div>
                  <p v-if="!group.rows.length" class="tf-movers__empty">None</p>
                  <div v-for="m in group.rows" :key="m.from + '→' + m.to" class="tf-mover"
                    @mouseenter="spotlightFlow(m.from, m.to)" @mouseleave="clearSpotlight">
                    <div class="tf-mover__label">
                      <span class="tf-mover__route">{{ m.from === m.to ? `${m.from} (internal)` : `${m.from} → ${m.to}` }}</span>
                      <span class="tf-mover__delta" :style="{ color: group.color }">
                        {{ m.delta > 0 ? '+' : '−' }}{{ fmtCompact(Math.abs(m.delta)) }}{{ moverUnits }}
                      </span>
                    </div>
                    <div class="tf-mover__bar-track">
                      <div class="tf-mover__bar" :style="{ width: `${Math.max(3, Math.abs(m.delta) / moverMax * 100)}%`, background: changeColor(m.prev, m.curr) }"></div>
                    </div>
                    <div class="tf-mover__sub">
                      {{ fmtCompact(m.prev) }} → {{ fmtCompact(m.curr) }}{{ moverUnits }}
                      <span>{{ pctLabel(m.prev, m.curr) }}</span>
                    </div>
                  </div>
                </div>
              </template>
            </aside>
          </div>
        </div>

      </div>
    </div>

  </div>

  <!-- Import + export region-country filters share one drawer sliding from
       the right, scoped to the chart area itself (:to teleports it into the
       chart container instead of <body>, and that container's CSS confines
       the drawer's own fixed-position mask/panel to its bounds - see
       .chord-canvas-wrap's `transform` below) rather than covering the whole
       page. -->
  <NDrawer :show="activeFilter === 'regionFilter'" @update:show="(v) => { if (!v) activeFilter = null; }"
    :to="captureDiv" :width="420" placement="right">
    <NDrawerContent closable>
      <template #header>
        <div class="tf-drawer-header">
          <span>Filters</span>
          <button v-if="effectiveFilter.length" class="tf-clear-link" @click="clearRegionFilter">Clear all</button>
        </div>
      </template>
      <div class="tf-drawer-body">
        <!-- Show other regions + Top N share the first line, Show rest of world
             sits under Show other regions (one or neither of the two is on);
             their explanations are tooltips -->
        <div class="tf-options-row">
          <div v-for="opt in visibleOthersOptions" :key="opt.mode" class="tf-option-label">
            <button :class="['tf-country-item tf-country-item--check tf-hover-toggle', { active: othersMode === opt.mode }]"
              :disabled="(opt.mode === 'world' && !hasSelection) || othersLockedByTopN"
              @click="toggleOthersMode(opt.mode)">
              <span class="tf-check-box">
                <svg v-if="othersMode === opt.mode" width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5l2.5 2.5 4.5-4" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </span>
              {{ opt.label }}
            </button>
            <NTooltip placement="bottom" :style="drawerTooltipStyle">
              <template #trigger>
                <span class="tf-info-icon" :aria-label="`About ${opt.label}`">
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="5.75" stroke="currentColor" stroke-width="1.2"/><path d="M6.5 5.8v3.4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><circle cx="6.5" cy="3.9" r="0.8" fill="currentColor"/></svg>
                </span>
              </template>
              {{ opt.mode === 'world' && !hasSelection ? 'Only available with Top N on or countries/regions selected - otherwise everything would be Rest of World. ' : '' }}{{ othersLockedByTopN ? 'Not available on the map with Top N on - only the trade between the top countries is shown. ' : '' }}{{ opt.hint }}
            </NTooltip>
          </div>
          <div class="tf-topn-inline">
            <span class="tf-panel-title">Top N</span>
            <NTooltip placement="bottom-end" :style="drawerTooltipStyle">
              <template #trigger>
                <span class="tf-info-icon" aria-label="About Top N">
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="5.75" stroke="currentColor" stroke-width="1.2"/><path d="M6.5 5.8v3.4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><circle cx="6.5" cy="3.9" r="0.8" fill="currentColor"/></svg>
                </span>
              </template>
              {{ topNHint }}
            </NTooltip>
            <div class="tf-chip-row">
              <button :class="['tf-chip', { active: !filterTopN }]" @click="setTopN(0)">Off</button>
              <button v-for="n in [5,10,15,20]" :key="n"
                :class="['tf-chip', { active: filterTopN === n, 'tf-chip--paused': filterTopN === n && effectiveFilter.length }]"
                @click="setTopN(n)">{{ n }}</button>
            </div>
          </div>
        </div>

        <div :class="['tf-drawer-section', { 'tf-drawer-section--collapsed': !drawerSectionOpen.selection }]">
          <div class="tf-panel-header tf-panel-header--start">
            <button class="tf-section-toggle" @click="drawerSectionOpen.selection = !drawerSectionOpen.selection">
              <svg class="tf-region-chevron" :class="{ 'tf-region-chevron--open': drawerSectionOpen.selection }"
                width="9" height="9" viewBox="0 0 9 9" fill="none"><path d="M2 1l4 3.5L2 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              <span class="tf-panel-title">Regions &amp; countries</span>
              <span v-if="effectiveFilter.length" class="tf-rail-badge tf-rail-badge--inline"><span class="tf-rail-badge__num">{{ effectiveFilter.length }}</span></span>
            </button>
            <NTooltip placement="bottom" :style="drawerTooltipStyle">
              <template #trigger>
                <span class="tf-info-icon" aria-label="About Regions & countries">
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="5.75" stroke="currentColor" stroke-width="1.2"/><path d="M6.5 5.8v3.4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><circle cx="6.5" cy="3.9" r="0.8" fill="currentColor"/></svg>
                </span>
              </template>
              {{ showOthers ? (focusSelection ? 'Highlights' : 'Splits out') : 'Only shows' }} trade in and out of the selected countries/regions.
              A selected region is broken out into its countries, so the trade between them is visible.
            </NTooltip>
            <!-- Focus: fade everything except the selection (manual picks or Top N) -->
            <div class="tf-option-label tf-focus-toggle">
              <button :class="['tf-country-item tf-country-item--check tf-hover-toggle', { active: focusSelection }]"
                :disabled="!showOthers"
                @click="toggleFocusSelection">
                <span class="tf-check-box">
                  <svg v-if="focusSelection" width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5l2.5 2.5 4.5-4" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </span>
                Focus
              </button>
              <NTooltip placement="bottom-end" :style="drawerTooltipStyle">
                <template #trigger>
                  <span class="tf-info-icon" aria-label="About Focus">
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="5.75" stroke="currentColor" stroke-width="1.2"/><path d="M6.5 5.8v3.4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><circle cx="6.5" cy="3.9" r="0.8" fill="currentColor"/></svg>
                  </span>
                </template>
                {{ focusHint }}
              </NTooltip>
            </div>
          </div>
          <template v-if="drawerSectionOpen.selection">
          <input v-model="filterSearch" placeholder="Search countries or regions…" class="tf-search-input" />
          <div class="tf-country-list modern-scrollbar">
            <div v-for="group in regionGroups" :key="group.region" class="tf-region-group">
              <button class="tf-region-header" @click="toggleRegionExpand(group.region)">
                <svg class="tf-region-chevron" :class="{ 'tf-region-chevron--open': isRegionOpen(group.region) }"
                  width="9" height="9" viewBox="0 0 9 9" fill="none"><path d="M2 1l4 3.5L2 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <span class="tf-check-box" :class="{ 'tf-check-box--checked': regionState(group) === 'full', 'tf-check-box--indeterminate': regionState(group) === 'partial' }" @click.stop="toggleFilterEntry('region', group.region)">
                  <svg v-if="regionState(group) === 'full'" width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5l2.5 2.5 4.5-4" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  <svg v-else-if="regionState(group) === 'partial'" width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5h6" stroke="#fff" stroke-width="1.5" stroke-linecap="round"/></svg>
                </span>
                <span class="tf-region-name">{{ group.region }}</span>
              </button>
              <div v-if="isRegionOpen(group.region)" class="tf-region-countries">
                <button v-for="name in group.countries" :key="name"
                  :class="['tf-country-item tf-country-item--check', { active: filterCountries.includes(name) }]"
                  @click="toggleFilterEntry('country', name)">
                  <span class="tf-check-box">
                    <svg v-if="filterCountries.includes(name)" width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5l2.5 2.5 4.5-4" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </span>
                  {{ name }}
                </button>
              </div>
            </div>
          </div>
          </template>
        </div>
      </div>
    </NDrawerContent>
  </NDrawer>

  <NDrawer v-model:show="showDisclaimer" :width="400" placement="right">
    <TermsAndConditions />
  </NDrawer>
</template>

<script setup>
import { ref, reactive, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { NSelect, NDatePicker, NButton, NDrawer, NDrawerContent, NTooltip } from 'naive-ui';
import * as d3 from 'd3';
import { sankey as d3Sankey, sankeyLinkHorizontal } from 'd3-sankey';
import * as htmlToImage from 'html-to-image';
import { SUBSCRIPTION_INFO } from '@/composables/subscription';
import { getTradeFlowHsCodes, getGlobalTradeFlowData, getCountryRegionMap, tradeFlowYearSpan } from '@/composables/marketData/datadashboards';
import { DDICONS, inputTheme } from '@/composables/generics';
import { loadWorldMap, drawFlowMap, pinTooltipLines, setFlowMapWidthScale } from '@/composables/marketData/worldMap';
import TermsAndConditions from './TermsAndConditions.vue';
import { logUserAction, USER_ACTIONS } from '@/composables/userLogs';

const RARE_EARTH_CODES = ['La', 'Ce', 'Pr', 'Nd', 'Sm', 'Eu', 'Gd', 'Tb', 'Dy', 'Ho', 'Er', 'Yb', 'Lu'];

// ── Constants ─────────────────────────────────────────────────────────────────
// Brand colour palette (on white): primary, secondary, tertiary.
const BRAND_PALETTE = [
  '#213CE5','#00AEEF','#009A46','#92D050','#F0C302','#FFED05', // primary
  '#6801FF','#9484FF',                                         // secondary
  '#F25904','#FC9860',                                         // tertiary
];

const randomId = Math.random().toString(36).slice(2);

// ── Filter state ──────────────────────────────────────────────────────────────
const FILTERS = reactive({ ELEMENT: null, PRODUCT: null });
const searchYear = ref(new Date(new Date().getFullYear() - 1, 0, 1).getTime());
const isLoading  = ref(false);
const hsLoading  = ref(false);
const showDisclaimer = ref(false);
const chartMenuOpen  = ref(false);

// ── Element / HS code options ─────────────────────────────────────────────────
const subscribedCodes = computed(() => SUBSCRIPTION_INFO?.elements?.map(r => r.code) ?? []);
const ELEMENT_OPTS = computed(() =>
  SUBSCRIPTION_INFO?.dataElements
    ?.filter(e => !RARE_EARTH_CODES.includes(e.code) || subscribedCodes.value.includes(e.code))
    ?.map(e => ({ label: e.name, value: e.code })) ?? []
);
const HS_CODE_OPTS = ref([]);

async function onElementChange(val) {
  FILTERS.PRODUCT = null;
  HS_CODE_OPTS.value = [];
  // Wipe the drawn chart too, otherwise the previous commodity's chart stays
  // painted over the "Select a commodity…" empty state.
  clearChart();
  if (!val) return;
  hsLoading.value = true;
  HS_CODE_OPTS.value = await getTradeFlowHsCodes(val, null);
  hsLoading.value = false;
}

// ── Year range ────────────────────────────────────────────────────────────────
// Like the other dashboards' date pickers, only years with data can be picked:
// the selected HS code's Start/End Year, or the whole commodity's before one is
// chosen. A selection that falls outside it (new commodity/HS code, or the
// initial last-calendar-year default) moves to the latest year with data.
const yearRange = computed(() => tradeFlowYearSpan(FILTERS.PRODUCT
  ? HS_CODE_OPTS.value.filter(o => o.value === FILTERS.PRODUCT)
  : HS_CODE_OPTS.value));

const yearOf = (ts) => new Date(ts).getFullYear();
const yearTs = (year) => new Date(year, 0, 1).getTime();
const isYearOutOfRange = (ts) => {
  const r = yearRange.value;
  return !!r && (yearOf(ts) < r.startYear || yearOf(ts) > r.endYear);
};

// A year in range other than `year` for the "vs" picker - the one before it,
// else the one after; null when the range is a single year.
function otherYearInRange(year) {
  const r = yearRange.value;
  if (!r) return year - 1;
  if (year - 1 >= r.startYear) return year - 1;
  if (year + 1 <= r.endYear) return year + 1;
  return null;
}

watch(yearRange, (r) => {
  if (!r) return;
  if (isYearOutOfRange(searchYear.value)) searchYear.value = yearTs(r.endYear);
  if (isYearOutOfRange(compareYear.value) || sameYear(compareYear.value, searchYear.value)) {
    const other = otherYearInRange(yearOf(searchYear.value));
    if (other != null) compareYear.value = yearTs(other);
  }
});

// ── Raw data + chord state ────────────────────────────────────────────────────
const rawRows    = ref([]);   // TradeFlowResult[] - "Volume" rows
const unitValueRows = ref([]); // TradeFlowResult[] - "Unit Value" rows (price per unit of the same flows)
const chordNodes = ref([]);   // [{ name, color }]
const countryRegionMap = ref({}); // country name → region, for grouping the chord by region
const lastYear   = ref(null); // year used for the last fetch, so filter changes can redraw without re-fetching

const hasData = computed(() => chordNodes.value.length > 0);
const hasSearched = computed(() => rawRows.value.length > 0);

// ── Refs ──────────────────────────────────────────────────────────────────────
const svgRef    = ref(null);
const svgBaseRef = ref(null); // side-by-side compare: the earlier year's chart
const captureDiv = ref(null);
const tooltipEl = ref(null);

// ── Region + country filter ───────────────────────────────────────────────────
// One selection shared by both sides of the trade - there's no separate import
// and export filter. A flow belongs to the selection when its exporter OR its
// importer is selected (a country passes if it's individually selected OR its
// whole region is). Selected countries are pulled out of their region as their
// own nodes, and a selected region is broken out into all of its countries, so
// trade between the selected countries (e.g. within Africa) is visible rather
// than collapsing into one region node. Empty = no manual selection (Top N
// applies instead). Mirrors the region ➜ country accordion filter on the
// CountryData trade flow view.
const chartView              = ref('chord'); // 'chord' | 'sankeynormal' | 'map'
const activeFilter          = ref(null); // 'regionFilter' | null
const filterRegions         = ref([]);
const filterCountries       = ref([]);
const drawerSectionOpen     = reactive({ selection: true });
const filterSearch          = ref('');
const expandedRegions       = ref([]);
// What happens to trade outside the selection (manual picks or Top N) - one or
// neither of the two toggles:
//   'regions' (Show other regions): kept, grouped into one node per region.
//   'world'   (Show rest of world): kept, summed into a single Rest of World node.
//   null: dropped - only the selection's trade is shown.
const othersMode             = ref('regions');
const showOthers             = computed(() => othersMode.value !== null);
const REST_OF_WORLD          = 'Rest of World';
const filterTopN             = ref(10); // auto-select the top N exporters + top N importers (0 = off) - see topNCountries
// Focus (with other trade shown): the selection (manual picks or Top N)
// and its trade stay bright, everything else fades. Off: nothing is faded.
const focusSelection         = ref(true);

const effectiveFilter = computed(() => [...filterCountries.value, ...filterRegions.value]);
// Something is split out (manual picks or Top N). Show rest of world needs
// this - without it every country would collapse into Rest of World - so it
// falls back to Show other regions once the selection is gone. flush 'sync'
// so the fallback lands before the redraw that follows the filter change.
const hasSelection = computed(() => effectiveFilter.value.length > 0 || !!filterTopN.value);
watch(hasSelection, (has) => {
  if (!has && othersMode.value === 'world') othersMode.value = 'regions';
}, { flush: 'sync' });
// Map with Top N driving the selection (not paused by manual picks): only the
// trade between the top countries is drawn, so Show other regions is turned
// off and locked until Top N is off or countries/regions are picked.
const othersLockedByTopN = computed(() => chartView.value === 'map' && !!filterTopN.value && !effectiveFilter.value.length);
watch(othersLockedByTopN, (locked) => {
  if (locked) othersMode.value = null;
}, { flush: 'sync', immediate: true });

// Filters drawer ⓘ tooltips. NTooltip teleports to <body>, out of reach of
// scoped styles, so the font is set inline to match the drawer's descriptions
// (.tf-panel-hint: 12px GilroyMedium).
const drawerTooltipStyle = {
  maxWidth: '260px',
  fontSize: '12px',
  fontFamily: "'GilroyMedium', sans-serif",
};
const focusHint = computed(() => !showOthers.value
  ? 'Only available with Show other regions or Show rest of world on - otherwise only the selection\'s trade is shown.'
  : 'On: the selected countries/regions (or Top N) and their trade stay bright, and everything else fades. Off: nothing is faded.');
const OFF_HINT = 'Off (with neither option on): only trade in and out of the selected countries/regions is shown (with Top N, only the trade between the top countries).';
const othersOptions = [
  { mode: 'regions', label: 'Show other regions',
    hint: 'On: all trade is shown. Selected (or Top N) countries are split out of their region total (and highlighted with Focus on), and everything else stays grouped by region. ' + OFF_HINT },
  { mode: 'world', label: 'Show rest of world',
    hint: 'On: all trade is shown. Selected (or Top N) countries are shown individually (and highlighted with Focus on), and everything else is summed into a single Rest of World ribbon. ' + OFF_HINT },
];
// The map plots individual countries only, so it has no Rest of World option
const visibleOthersOptions = computed(() => chartView.value === 'map'
  ? othersOptions.filter(opt => opt.mode !== 'world')
  : othersOptions);
const topNHint = computed(() => {
  if (filterTopN.value && effectiveFilter.value.length) return 'Top N is paused while countries or regions are selected.';
  const n = filterTopN.value || 'N';
  const others = othersMode.value === 'world' ? 'everyone else is grouped as Rest of World.' : 'everyone else is grouped by region.';
  return `Picks the top ${n} exporting and top ${n} importing countries and `
    + (!showOthers.value ? 'shows only the trade between them.'
      : focusSelection.value ? `highlights the trade between them - ${others}`
      : `splits them out - ${others}`);
});

// True when a country is in the manual selection (itself or its whole region).
function isPickedCountry(country) {
  return filterCountries.value.includes(country)
    || filterRegions.value.includes(countryRegionMap.value[country] || 'Other');
}

// Only countries actually present in the current search results (as exporter
// or importer) can be filtered by — same as CountryData's trade flow filters,
// which build their lists from the fetched Sankey data rather than every
// known country.
const partnerNames = computed(() => [...new Set(rawRows.value.flatMap(r => [r.country, r.tradePartner]).filter(Boolean))].sort());

// Alphabetical, region-grouped accordion data.
function buildRegionGroups(partnerNames, regionMap, search) {
  const q = search.toLowerCase();
  const byRegion = {};
  partnerNames.forEach(country => {
    const region = regionMap[country] || 'Other';
    (byRegion[region] ??= []).push(country);
  });
  return Object.keys(byRegion).sort().map(region => ({
    region,
    countries: byRegion[region].sort(),
  })).map(group => {
    if (!q) return group;
    const regionMatches = group.region.toLowerCase().includes(q);
    const countries = regionMatches ? group.countries : group.countries.filter(n => n.toLowerCase().includes(q));
    return { region: group.region, countries };
  }).filter(group => group.countries.length);
}
const regionGroups = computed(() => buildRegionGroups(partnerNames.value, countryRegionMap.value, filterSearch.value));

function toggleRegionExpand(region) {
  const idx = expandedRegions.value.indexOf(region);
  expandedRegions.value = idx === -1 ? [...expandedRegions.value, region] : expandedRegions.value.filter(n => n !== region);
}

// A region showing because of an active search is a TEMPORARY visibility
// state, not a persisted one — it must not be baked into expandedRegions,
// or clearing the search leaves every region stuck open forever (since
// nothing ever removes them again except one-by-one manual clicks).
// Expand/collapse is otherwise driven ONLY by the chevron (toggleRegionExpand)
// — selecting a region/country as a filter must not also expand or collapse
// its accordion section, those are two separate actions.
const isRegionOpen = (region) => !!filterSearch.value || expandedRegions.value.includes(region);

function regionState(group) {
  if (group.countries.some(n => filterCountries.value.includes(n))) return 'partial';
  if (filterRegions.value.includes(group.region)) return 'full';
  return 'none';
}

const _filterRefs = { region: filterRegions, country: filterCountries };
function toggleFilter(name) {
  activeFilter.value = activeFilter.value === name ? null : name;
}
function toggleFilterEntry(key, name) {
  const r = _filterRefs[key];
  const idx = r.value.indexOf(name);
  r.value = idx === -1 ? [...r.value, name] : r.value.filter(n => n !== name);
  if (lastYear.value) redraw();
}
// The two "others" toggles are exclusive: turning one on turns the other off,
// and clicking the active one turns both off.
function toggleOthersMode(mode) {
  othersMode.value = othersMode.value === mode ? null : mode;
  if (lastYear.value) redraw();
}
function clearRegionFilter() {
  filterRegions.value = [];
  filterCountries.value = [];
  if (lastYear.value) redraw();
}

// ── Top N ──────────────────────────────────────────────────────────────────────
function setTopN(n) {
  filterTopN.value = n;
  if (lastYear.value) redraw();
}


// ── Focus ────────────────────────────────────────────────────────────────────
// The selection (manual picks or Top N) is what gets focused - there's no
// separate list. The highlight is computed in prepareFlowData/buildMap, so a
// redraw picks the new setting up.
function toggleFocusSelection() {
  focusSelection.value = !focusSelection.value;
  if (lastYear.value) redraw();
}

function resetFilters() {
  filterTopN.value = 10;
  focusSelection.value = true;
  filterRegions.value = [];
  filterCountries.value = [];
  othersMode.value = othersLockedByTopN.value ? null : 'regions';
  filterSearch.value = '';
  activeFilter.value = null;
  if (lastYear.value) redraw();
}

// ── Fetch ─────────────────────────────────────────────────────────────────────
async function fetchChord() {
  if (!FILTERS.ELEMENT || !FILTERS.PRODUCT) return;
  isLoading.value = true;
  clearChart();

  // Comparing: one fetch spans both years. lastYear is always the LATER year
  // and lastBaseYear the earlier one, so "change" always reads forward in time
  // whichever picker holds which year.
  const picked = new Date(searchYear.value).getFullYear();
  const other = compareMode.value ? new Date(compareYear.value).getFullYear() : picked;
  const year = String(Math.max(picked, other));
  const baseYear = other !== picked ? String(Math.min(picked, other)) : null;
  lastYear.value = year;
  lastBaseYear.value = baseYear;

  // Track Trade Flow search query
  logUserAction(
    USER_ACTIONS.SEARCH_TRADE_FLOW,
    `Searched Trade Flow: Commodity="${FILTERS.ELEMENT}", HS Code="${FILTERS.PRODUCT}", Year=${year}${baseYear ? ` vs ${baseYear}` : ''}`
  );

  const [rows] = await Promise.all([
    getGlobalTradeFlowData(FILTERS.ELEMENT, FILTERS.PRODUCT, baseYear ?? year, year),
    Object.keys(countryRegionMap.value).length ? null : getCountryRegionMap().then(map => { countryRegionMap.value = map; }),
  ]);

  rawRows.value = rows.filter(r => r.productVariety?.toLowerCase() === 'volume');
  unitValueRows.value = rows.filter(r => r.productVariety?.toLowerCase() === 'unit value');

  isLoading.value = false;
  await nextTick();
  redraw();
}

function setChartView(v) {
  chartView.value = v;
  // Track chart view switch (chord, sankey, map)
  logUserAction(
    USER_ACTIONS.VIEW_TRADE_FLOW,
    `Switched Trade Flow view to: "${v}" (Commodity: "${FILTERS.ELEMENT || 'N/A'}", HS Code: "${FILTERS.PRODUCT || 'N/A'}")`
  );
  // The map only offers Show other regions (it can't draw a Rest of World node)
  if (v === 'map' && othersMode.value === 'world') othersMode.value = 'regions';
  activeFilter.value = null;
  if (lastYear.value) nextTick(() => redraw());
}

// Draws the current view: the searched year, or when comparing either the
// Change view (the later year coloured by its change since the earlier one)
// or both years side by side.
function redraw({ animate = false } = {}) {
  const year = lastYear.value, baseYear = lastBaseYear.value;
  if (!year) return;
  compareSummary.value = null;
  splitTotals.value = null;
  if (svgBaseRef.value) d3.select(svgBaseRef.value).selectAll('*').remove();
  if (chartView.value === 'map') buildMap(year, baseYear);
  else if (showSplit.value) buildSideBySide(year, baseYear);
  else if (chartView.value === 'sankeynormal') buildSankey(year, baseYear);
  else buildChord(year, baseYear);
  if (animate) {
    d3.selectAll(chartSvgs()).interrupt().style('opacity', 0)
      .transition().duration(350).ease(d3.easeCubicOut).style('opacity', 1);
  }
}

// Every chart SVG on screen (two when side by side).
const chartSvgs = () => [svgRef.value, showSplit.value ? svgBaseRef.value : null].filter(Boolean);

// ── Year comparison ───────────────────────────────────────────────────────────
// With Compare on, a search fetches two years, shown one of two ways:
// - Change: one chart sized by the later year and coloured on a diverging
//   decline ↔ growth scale by its change since the earlier year (tooltips
//   carry both years' values), with a "Biggest movers" panel beside it.
// - Side by side (chord/Sankey only): both years' own charts, built on the
//   same nodes, colours and scale so they can be compared directly. The map
//   always shows Change.
const compareMode    = ref(false);
const compareYear    = ref(new Date(new Date().getFullYear() - 2, 0, 1).getTime());
const lastBaseYear   = ref(null);     // earlier year of the last compare search (null = not comparing)
const yearView       = ref('change'); // 'change' | 'both'
const compareSummary = ref(null);     // { baseYear, year, units, totals, movers } from the last Change build
const splitTotals    = ref(null);     // { prev, curr, units } from the last side-by-side build
const isComparing    = computed(() => hasSearched.value && !!lastBaseYear.value);
// Both are known BEFORE a build (unlike compareSummary/splitTotals), so the
// layout is already settled when the builders measure the SVGs' width -
// switching views redraws on nextTick.
const showSplit      = computed(() => isComparing.value && yearView.value === 'both' && chartView.value !== 'map');
const showMovers     = computed(() => isComparing.value && !showSplit.value);
const splitHeads     = computed(() => splitTotals.value ? [
  { key: 'prev', year: lastBaseYear.value, total: splitTotals.value.prev },
  { key: 'curr', year: lastYear.value, total: splitTotals.value.curr },
] : []);

function setYearView(v) {
  if (yearView.value === v) return;
  yearView.value = v;
  hideTip();
  nextTick(() => redraw({ animate: true }));
}

const sameYear = (a, b) => a != null && b != null && new Date(a).getFullYear() === new Date(b).getFullYear();

function toggleCompareMode() {
  compareMode.value = !compareMode.value;
  yearView.value = 'change';
  // Comparing a year with itself is meaningless - start the "vs" year one
  // earlier (or later, at the start of the data), within the years with data.
  if (compareMode.value && (sameYear(searchYear.value, compareYear.value) || isYearOutOfRange(compareYear.value))) {
    const other = otherYearInRange(yearOf(searchYear.value));
    if (other != null) compareYear.value = yearTs(other);
  }
  if (!hasSearched.value) return;
  if (compareMode.value) {
    fetchChord();
  } else {
    // The later year's data is already loaded - just stop comparing.
    lastBaseYear.value = null;
    nextTick(() => redraw({ animate: true }));
  }
}

// Diverging decline ↔ growth scale on % change. sqrt-compressed so moderate
// moves (±10-30%) are clearly tinted rather than near-grey, saturating at
// ±100%. Flows that didn't exist in the earlier year count as full growth.
const CHANGE_COLORS = { decline: '#F25904', flat: '#c9ced8', growth: '#009A46' };
// piecewise (not interpolateRgbBasis, a spline that misses the midpoint) so 0% is exactly the flat grey
const changeInterp = d3.piecewise(d3.interpolateRgb, [CHANGE_COLORS.decline, CHANGE_COLORS.flat, CHANGE_COLORS.growth]);
const changeT = (pct) => Math.sign(pct) * Math.sqrt(Math.min(1, Math.abs(pct))); // -1..1
function pctChange(prev, curr) {
  if (!prev) return curr ? Infinity : 0;
  return (curr - prev) / prev;
}
function changeColor(prev, curr) {
  const p = pctChange(prev, curr);
  return p === Infinity ? CHANGE_COLORS.growth : changeInterp((changeT(p) + 1) / 2);
}
const CHANGE_GRADIENT_CSS = `linear-gradient(90deg, ${d3.range(0, 1.0001, 0.1).map(t => changeInterp(t)).join(', ')})`;
const CHANGE_TICKS = [-1, -0.25, 0, 0.25, 1].map(p => ({
  pos: (changeT(p) + 1) / 2,
  label: p === 0 ? '0%' : `${p > 0 ? '+' : '−'}${Math.abs(p * 100)}%${p === 1 ? '+' : ''}`,
}));

const fmtCompact = (v) => Math.abs(v) < 1000
  ? v.toLocaleString('en', { maximumFractionDigits: 1 })
  : d3.format('.3~s')(v).replace('G', 'B');
function pctLabel(prev, curr) {
  const p = pctChange(prev, curr);
  if (p === Infinity) return 'New';
  if (prev && !curr) return 'Stopped';
  return `${p >= 0 ? '+' : '−'}${Math.abs(p * 100).toLocaleString('en', { maximumFractionDigits: Math.abs(p) < 0.1 ? 1 : 0 })}%`;
}

// Tooltip lines: earlier → later value, then the change in the scale's colour
// (lightened variants, readable on the dark tooltip).
function changeTipLines(prev, curr, units) {
  const fmt = v => v.toLocaleString('en', { maximumFractionDigits: 1 });
  const u = units ? ` ${units}` : '';
  const delta = curr - prev;
  const tone = pctChange(prev, curr) > 0.005 ? '#6fe0a0' : pctChange(prev, curr) < -0.005 ? '#ffa36e' : '#d5dae4';
  return `<br/><span style="opacity:.75">${lastBaseYear.value}:</span> ${fmt(prev)}${u}`
    + `<br/><span style="opacity:.75">${lastYear.value}:</span> ${fmt(curr)}${u}`
    + `<br/><strong style="color:${tone}">${delta >= 0 ? '▲ +' : '▼ −'}${fmt(Math.abs(delta))}${u} (${pctLabel(prev, curr)})</strong>`;
}

// Ranks flows by absolute change into the top gainers and decliners.
function rankMovers(flows, limit = 6) {
  const withDelta = flows.map(f => ({ ...f, delta: f.curr - f.prev })).filter(f => f.delta);
  return {
    gainers: withDelta.filter(f => f.delta > 0).sort((a, b) => b.delta - a.delta).slice(0, limit),
    losers:  withDelta.filter(f => f.delta < 0).sort((a, b) => a.delta - b.delta).slice(0, limit),
  };
}
const moverGroups = computed(() => {
  const m = compareSummary.value?.movers;
  if (!m) return [];
  return [
    { key: 'gainers', label: 'Biggest gains', color: CHANGE_COLORS.growth, rows: m.gainers },
    { key: 'losers', label: 'Biggest declines', color: CHANGE_COLORS.decline, rows: m.losers },
  ];
});
const moverUnits = computed(() => compareSummary.value?.units ? ' ' + compareSummary.value.units : '');
const moverMax = computed(() => d3.max(moverGroups.value.flatMap(g => g.rows), m => Math.abs(m.delta)) || 1);

// Spotlights the flows matching isLit(from, to) on every chord/Sankey on
// screen. Used by the movers panel (one flow) and, side by side, by hovering
// a node in either chart (all of that node's trade, lit in both years).
function spotlight(isLit, isLitNode) {
  if (chartView.value === 'map') return;
  for (const svgEl of chartSvgs()) {
    const svg = d3.select(svgEl);
    if (chartView.value === 'chord') {
      const names = svgEl.__chord?.names ?? [];
      svg.selectAll('.chord-ribbon').attr('opacity', d => isLit(names[d.source.index], names[d.target.index]) ? 1 : 0.06);
      svg.selectAll('.chord-arc').attr('opacity', d => isLitNode(names[d.index]) ? 1 : 0.25);
    } else {
      svg.selectAll('.sankey-link').attr('stroke-opacity', d => isLit(d.from, d.to) ? 0.9 : 0.04);
    }
  }
}
const spotlightFlow = (from, to) => spotlight((a, b) => a === from && b === to, n => n === from || n === to);
const spotlightNode = (name) => spotlight((a, b) => a === name || b === name, n => n === name);
function clearSpotlight() {
  for (const svgEl of chartSvgs()) {
    if (chartView.value === 'chord') applyChordFocus(svgEl);
    else if (chartView.value === 'sankeynormal') d3.select(svgEl).selectAll('.sankey-link').attr('stroke-opacity', d => d.baseOpacity);
  }
}

// ── Side by side ──────────────────────────────────────────────────────────────
// Both years are built on the later year's Top N (so the same countries are
// split out in each), with one shared colour per node (ranked on the later
// year) and one shared scale, so sizes compare across the two charts:
// - Chord: each ring's AREA is proportional to that year's total trade (the
//   larger year fills the space; floored at 30% radius to stay legible).
// - Sankey: the same pixels-per-unit in both - each chart is laid out on its
//   own, then the one with the larger scale is rebuilt at the smaller.
function buildSideBySide(year, baseYear) {
  const curr = prepareFlowData(year, null, { topYear: year });
  const prev = prepareFlowData(baseYear, null, { topYear: year });
  const totalOf = (data) => data ? d3.sum(data.directed, row => d3.sum(row)) : 0;
  const totals = { prev: totalOf(prev), curr: totalOf(curr) };
  splitTotals.value = { ...totals, units: (curr ?? prev)?.units || '' };

  const ref = curr ?? prev;
  if (!ref) { chordNodes.value = []; return; }
  const isSankey = chartView.value === 'sankeynormal';
  const colorMap = isSankey
    ? sankeyColorMap(ref)
    : new Map(ref.names.map((name, i) => [name, ref.colorScale(i)]));
  const charts = [
    { svgEl: svgBaseRef.value, year: baseYear, data: prev },
    { svgEl: svgRef.value, year, data: curr },
  ];

  if (isSankey) {
    const scales = charts.map(c => buildSankey(c.year, null, { svgEl: c.svgEl, data: c.data, colorMap }));
    const shared = d3.min(scales.filter(Boolean));
    charts.forEach((c, i) => {
      if (scales[i] && scales[i] > shared * 1.001) buildSankey(c.year, null, { svgEl: c.svgEl, data: c.data, colorMap, maxScale: shared });
    });
  } else {
    const maxTotal = Math.max(totals.prev, totals.curr) || 1;
    charts.forEach(c => buildChord(c.year, null, {
      svgEl: c.svgEl, data: c.data, colorMap,
      radiusScale: Math.max(0.3, Math.sqrt(totalOf(c.data) / maxTotal)),
    }));
  }
  // hasData: either year has something to show.
  const names = [...new Set([...(prev?.names ?? []), ...(curr?.names ?? [])])];
  chordNodes.value = names.map(name => ({ name, color: colorMap.get(name) ?? NO_COLOR }));
}

// Colour for a node with no shared colour (only traded in the earlier year).
const NO_COLOR = '#9dabaf';

// Draws a centred "no trade" note into a side-by-side chart with nothing to show.
function drawEmptyNote(svgEl, year) {
  const W = svgEl.clientWidth || 600, H = svgEl.clientHeight || 520;
  d3.select(svgEl).append('text')
    .attr('x', W / 2).attr('y', H / 2).attr('text-anchor', 'middle')
    .attr('font-size', 13).attr('fill', '#9dabaf')
    .attr('font-family', "'GilroyMedium', system-ui, sans-serif")
    .text(`No trade flows in ${year} for the current filters`);
}

// ── Unit values ───────────────────────────────────────────────────────────────
// "Unit Value" rows carry the price per unit for the same exporter → partner
// flow as a "Volume" row. When several country flows roll up into one ribbon
// (region nodes), the ribbon's unit value is the volume-weighted average of
// the flows that have one - shown as "Avg unit value" so it isn't mistaken for
// a single reported price.
// Unit values are matched to the volume row of the same flow - same exporter,
// partner AND HS code - so each one is weighted by its own flow's volume.
function unitValueLookup(year) {
  const map = new Map(); // 'exporter|||partner|||hsCode' → { value, units }
  for (const row of unitValueRows.value) {
    if (!(row.typeName || '').toLowerCase().includes('export')) continue;
    const v = row.yearlyData?.[year];
    if (v == null || !Number(v)) continue;
    map.set(`${row.country}|||${row.tradePartner}|||${row.productForm}`, { value: Number(v), units: row.units || '' });
  }
  return (volumeRow) => map.get(`${volumeRow.country}|||${volumeRow.tradePartner}|||${volumeRow.productForm}`) ?? null;
}

// Accumulates volume-weighted unit values per key; get(key) → { value, units, averaged } | null.
function unitValueAccumulator() {
  const acc = new Map();
  return {
    add(key, uv, volume) {
      if (!uv) return;
      const a = acc.get(key) || { weighted: 0, volume: 0, count: 0, units: uv.units };
      a.weighted += uv.value * volume;
      a.volume += volume;
      a.count += 1;
      acc.set(key, a);
    },
    get(key) {
      const a = acc.get(key);
      return a && a.volume ? { value: a.weighted / a.volume, units: a.units, averaged: a.count > 1 } : null;
    },
  };
}

const unitValueLine = (uv) => uv
  ? `<br/>${uv.averaged ? 'Avg unit value' : 'Unit value'}: ${uv.value.toLocaleString('en', { maximumFractionDigits: 2 })}${uv.units ? ' ' + uv.units : ''}`
  : '';

// ── Shared node/edge construction (Chord + Sankey both render this) ────────────
// Nodes are normally REGIONS (country rows rolled up to region totals). But a
// country that's been individually selected in either filter panel is pulled
// out of its region and shown as its own node instead — same "shown
// separately, excluded from the region total" behaviour as the CountryData
// trade flow view. That also means trade between two countries in the same
// region becomes visible whenever at least one side resolves to a distinct
// node (an individually-picked country, or that country vs. its own region's
// remainder) — it's only dropped as a self-loop when both sides still
// collapse to the exact same node (i.e. neither is individually selected).
// Returns null when there's nothing to show.
// baseYear (Change view): the earlier year's trade is collected through the
// same node mapping (Top N / selection still come from `year`), so every
// node and flow can be compared like-for-like - see `change` below.
// topYear (side by side): pick the Top N from this year instead of `year`,
// so both years' charts split out the same countries.
function prepareFlowData(year, baseYear = null, { topYear = year } = {}) {
  const regionMap = countryRegionMap.value;
  const regionOf = (country) => regionMap[country] || 'Other';
  // Manual picks take over from Top N while any are set.
  const manualActive = effectiveFilter.value.length > 0;
  const top = manualActive ? topNCountries(topYear, { off: true }) : topNCountries(topYear);
  const selectionActive = manualActive || top.active;
  // Every picked country is its own node - including every country of a picked
  // region, which is what keeps trade between them (e.g. within Africa)
  // visible instead of collapsing into a single region self-loop.
  const isSplitOut = (country) => isPickedCountry(country) || top.all.has(country);
  // Everyone else: their region's node, or one Rest of World node.
  const othersNodeOf = othersMode.value === 'world' ? () => REST_OF_WORLD : regionOf;
  const nodeOf = (country) => isSplitOut(country) ? country : othersNodeOf(country);
  const units = rawRows.value.find(r => r.units)?.units || '';

  const flowMap = new Map(); // 'NodeA→NodeB' => volume
  const selfFlowMap = new Map(); // node → total intra-node (e.g. intra-region) trade volume
  const selectedFlowKeys = new Set(); // 'NodeA|||NodeB' keys carrying the selection's trade (self-loops: 'A|||A')
  const uvOfRow = unitValueLookup(year);
  const flowUv = unitValueAccumulator(); // keyed like flowMap
  const selfUv = unitValueAccumulator(); // keyed like selfFlowMap
  const prevFlowMap = new Map(); // like flowMap, for baseYear
  const prevSelfMap = new Map(); // like selfFlowMap, for baseYear
  for (const row of rawRows.value) {
    const src = row.country;        // exporter
    const dst = row.tradePartner;   // importer / destination
    if (!src || !dst || src === dst) continue;
    const typeName = (row.typeName || '').toLowerCase();
    // Use export rows: country exports TO partner
    if (!typeName.includes('export')) continue;
    const val = Number(row.yearlyData?.[year] ?? 0);
    const prev = baseYear ? Number(row.yearlyData?.[baseYear] ?? 0) : 0;
    if (!val && !prev) continue;
    // Manual picks: the flow is exported OR imported by a picked country.
    const inSelection = manualActive ? (isPickedCountry(src) || isPickedCountry(dst)) : top.passes(src, dst);
    // With "Show other regions" / "Show rest of world" on, the selection
    // (manual picks or Top N) only splits its countries out (nodeOf) and
    // highlights its trade - no flow is dropped. Off: only the selection's
    // trade is kept.
    if (!showOthers.value && !inSelection) continue;
    const nodeA = nodeOf(src);
    const nodeB = nodeOf(dst);
    if (selectionActive && inSelection) selectedFlowKeys.add(`${nodeA}|||${nodeB}`);
    if (nodeA === nodeB) {
      // Both sides collapse to the same node (e.g. two countries in a region
      // that hasn't been split out) — there's nowhere to draw a two-ended
      // ribbon, but the trade is real, so it's kept as a self-loop total
      // instead of being silently dropped.
      if (prev) prevSelfMap.set(nodeA, (prevSelfMap.get(nodeA) || 0) + prev);
      if (!val) continue;
      selfFlowMap.set(nodeA, (selfFlowMap.get(nodeA) || 0) + val);
      selfUv.add(nodeA, uvOfRow(row), val);
      continue;
    }
    const key = `${nodeA}|||${nodeB}`;
    if (prev) prevFlowMap.set(key, (prevFlowMap.get(key) || 0) + prev);
    if (!val) continue;
    flowMap.set(key, (flowMap.get(key) || 0) + val);
    flowUv.add(key, uvOfRow(row), val);
  }

  if (!flowMap.size && !selfFlowMap.size) return null;

  // Collect unique node names
  const nameSet = new Set();
  for (const [key] of flowMap) {
    const [a, b] = key.split('|||');
    nameSet.add(a);
    nameSet.add(b);
  }
  for (const name of selfFlowMap.keys()) nameSet.add(name);
  let names = [...nameSet].sort();

  // Group nodes by region so a split-out country always sits next to its own
  // region's remainder node instead of falling wherever its name sorts
  // alphabetically. Rest of World always goes last.
  const regionKeyOf = (node) => regionMap[node] || node; // country → its region; region-remainder → itself
  names = names.sort((a, b) => {
    if ((a === REST_OF_WORLD) !== (b === REST_OF_WORLD)) return a === REST_OF_WORLD ? 1 : -1;
    const ra = regionKeyOf(a), rb = regionKeyOf(b);
    if (ra !== rb) return ra.localeCompare(rb);
    const aIsRemainder = a === ra, bIsRemainder = b === rb;
    if (aIsRemainder !== bIsRemainder) return aIsRemainder ? -1 : 1;
    return a.localeCompare(b);
  });

  const n = names.length;
  const idx = Object.fromEntries(names.map((nm, i) => [nm, i]));

  // Directed n×n matrix: directed[i][j] is region i's export volume to region j.
  // Kept in raw units for tooltips and group totals. The diagonal carries
  // intra-region ("self-loop") trade — d3.chordDirected() natively supports
  // diagonal entries and gives them their own reserved slice of the group's
  // angle (which correctly makes the group's own arc bigger to fit it), so
  // self-trade must go IN the matrix, not be handled as a decorative overlay
  // added on top of an already-fully-allocated arc (which is what a fixed-
  // width self-loop drawn after the fact would otherwise overlap).
  const directed = Array.from({ length: n }, () => new Array(n).fill(0));
  for (const [key, val] of flowMap) {
    const [a, b] = key.split('|||');
    if (!(a in idx) || !(b in idx)) continue;
    directed[idx[a]][idx[b]] += val;
  }
  const selfTotal = names.map(name => selfFlowMap.get(name) || 0);
  selfTotal.forEach((val, i) => { if (val > 0) directed[i][i] = val; });

  // Colours by volume, not position: the node with the most trade (exports +
  // imports; the diagonal counts once) always gets the primary brand colour,
  // the next biggest the next palette colour, and so on.
  const nodeTotal = names.map((_, i) => directed[i].reduce((s, v) => s + v, 0) + directed.reduce((s, row) => s + row[i], 0) - directed[i][i]);
  const colorRank = new Array(n);
  [...names.keys()].sort((a, b) => nodeTotal[b] - nodeTotal[a]).forEach((i, rank) => { colorRank[i] = rank; });
  const colorScale = (i) => BRAND_PALETTE[colorRank[i] % BRAND_PALETTE.length];

  // Unit value for the directed[i][j] flow (i === j: internal trade).
  const unitValueOf = (i, j) => i === j
    ? selfUv.get(names[i])
    : flowUv.get(`${names[i]}|||${names[j]}`);

  // Selection highlight (Focus on, and only when the rest of world is still
  // shown - otherwise the selection's trade is all there is): the selected
  // nodes (picked countries/regions, or the Top N countries), and the
  // node→node flows carrying their trade. null = nothing to highlight.
  const isSelectedNode = manualActive
    ? (name) => isPickedCountry(name)
    : (name) => top.all.has(name);
  const highlight = selectionActive && showOthers.value && focusSelection.value
    ? { nodes: new Set(names.filter(isSelectedNode)), isFlow: (a, b) => selectedFlowKeys.has(`${a}|||${b}`) }
    : null;

  return { names, idx, directed, selfTotal, units, colorScale, unitValueOf, highlight,
    change: baseYear ? buildFlowChange(names, flowMap, selfFlowMap, prevFlowMap, prevSelfMap, units, year, baseYear) : null };
}

// Change view data for prepareFlowData's nodes. Per-node earlier-year exports
// and imports are summed like the directed matrix's rows/columns (internal
// trade counts on both sides), so they compare directly with the drawn totals.
// Movers and totals also cover flows that stopped entirely - those have no
// ribbon to draw, but are still part of the story.
function buildFlowChange(names, flowMap, selfFlowMap, prevFlowMap, prevSelfMap, units, year, baseYear) {
  const prevExports = new Map(), prevImports = new Map();
  const addPrev = (a, b, v) => {
    prevExports.set(a, (prevExports.get(a) || 0) + v);
    prevImports.set(b, (prevImports.get(b) || 0) + v);
  };
  for (const [key, v] of prevFlowMap) addPrev(...key.split('|||'), v);
  for (const [node, v] of prevSelfMap) addPrev(node, node, v);

  const flows = [];
  for (const key of new Set([...flowMap.keys(), ...prevFlowMap.keys()])) {
    const [from, to] = key.split('|||');
    flows.push({ from, to, prev: prevFlowMap.get(key) || 0, curr: flowMap.get(key) || 0 });
  }
  for (const node of new Set([...selfFlowMap.keys(), ...prevSelfMap.keys()])) {
    flows.push({ from: node, to: node, prev: prevSelfMap.get(node) || 0, curr: selfFlowMap.get(node) || 0 });
  }

  return {
    flowPrev: (i, j) => i === j ? (prevSelfMap.get(names[i]) || 0) : (prevFlowMap.get(`${names[i]}|||${names[j]}`) || 0),
    nodePrev: names.map(name => ({
      exports: prevExports.get(name) || 0,
      imports: prevImports.get(name) || 0,
      self: prevSelfMap.get(name) || 0,
    })),
    summary: {
      baseYear,
      year,
      units,
      totals: { prev: d3.sum(flows, f => f.prev), curr: d3.sum(flows, f => f.curr) },
      movers: rankMovers(flows),
    },
  };
}

// ── Top N auto-filter ─────────────────────────────────────────────────────────
// The N biggest exporting countries and the N biggest importing countries
// (by total volume for the year, across all partners). They're split out of
// their own nodes (everyone else stays grouped by region), and a flow counts
// as "Top N trade" when it runs between two top countries. filterTopN = 0 (or
// `off`, used while manual picks are set) turns it off.
function topNCountries(year, { off = false } = {}) {
  const n = off ? 0 : filterTopN.value;
  if (!n) return { active: false, exporters: new Set(), importers: new Set(), all: new Set(), passes: () => true };
  const expTotals = new Map(), impTotals = new Map();
  for (const row of rawRows.value) {
    const src = row.country, dst = row.tradePartner;
    if (!src || !dst || src === dst) continue;
    if (!(row.typeName || '').toLowerCase().includes('export')) continue;
    const val = Number(row.yearlyData?.[year] ?? 0);
    if (!val) continue;
    expTotals.set(src, (expTotals.get(src) || 0) + val);
    impTotals.set(dst, (impTotals.get(dst) || 0) + val);
  }
  const topOf = (totals) => new Set([...totals].sort((a, b) => b[1] - a[1]).slice(0, n).map(([name]) => name));
  const exporters = topOf(expTotals), importers = topOf(impTotals);
  const all = new Set([...exporters, ...importers]);
  return {
    active: true,
    exporters,
    importers,
    all,
    passes: (src, dst) => all.has(src) && all.has(dst),
  };
}

// The map view attaches d3.zoom to svgEl itself, and its wheel listener
// swallows page scrolling. Like the viewBox (see buildSankey), those listeners
// survive clearing the children, so the chord/Sankey views must detach them or
// the page can't be scrolled with the cursor over the chart after visiting
// the map.
function removeMapZoom(svgEl) {
  d3.select(svgEl).on('.zoom', null);
}

// ── Build chord diagram ───────────────────────────────────────────────────────
// Side by side passes `opts`: the target svgEl, its prepared data, the shared
// colorMap (name → colour) and radiusScale (ring size relative to the space).
let chordUid = 0; // keeps <textPath> ids unique when two chords are on the page
function buildChord(year, baseYear = null, opts = {}) {
  const svgEl = opts.svgEl ?? svgRef.value;
  if (!svgEl) return;
  d3.select(svgEl).selectAll('*').remove();
  removeMapZoom(svgEl);

  const data = 'data' in opts ? opts.data : prepareFlowData(year, baseYear);
  // Per-SVG state for applyChordFocus/spotlight (two chords side by side)
  svgEl.__chord = { names: data?.names ?? [], highlight: data?.highlight ?? null };
  if (!data) {
    chordNodes.value = [];
    if (opts.svgEl) drawEmptyNote(svgEl, year);
    return;
  }
  const { names, directed, selfTotal, units, unitValueOf, change } = data;
  compareSummary.value = change?.summary ?? null;
  const uid = ++chordUid;

  // Change view: arcs by each node's total trade change (exports + imports,
  // internal counted once - same as the colour ranking), ribbons by their own
  // flow's change. Otherwise the usual brand colours by volume.
  const exportsOf = (i) => directed[i].reduce((s, v) => s + v, 0);
  const importsOf = (i) => directed.reduce((s, row) => s + row[i], 0);
  const colorScale = change
    ? (i) => {
      const p = change.nodePrev[i];
      return changeColor(p.exports + p.imports - p.self, exportsOf(i) + importsOf(i) - directed[i][i]);
    }
    : opts.colorMap ? (i) => opts.colorMap.get(names[i]) ?? NO_COLOR
    : data.colorScale;
  const flowColor = change
    ? (i, j) => changeColor(change.flowPrev(i, j), directed[i][j])
    : (i) => colorScale(i);
  chordNodes.value = names.map((name, i) => ({ name, color: colorScale(i) }));
  // The chord always uses true linear proportions - it exists specifically to
  // show relative scale at a glance (the biggest flow should visibly dominate),
  // so compressing that via log scaling would defeat the point. (Every view on
  // this page is linear.)
  const scaled = directed;

  // ── D3 chord layout ─────────────────────────────────────────────────────────
  const W = svgEl.clientWidth  || 600;
  const H = svgEl.clientHeight || 560;
  const R = (Math.min(W, H) / 2 - 90) * (opts.radiusScale ?? 1);

  // d3.chordDirected() keeps matrix[i][j] and matrix[j][i] as two separate
  // ribbons (rather than merging them into one bidirectional ribbon like
  // d3.chord()), so each direction of trade gets its own tapered ribbon.
  const chord = d3.chordDirected().padAngle(0.03).sortSubgroups(d3.descending);
  const chords = chord(scaled);

  const arc = d3.arc().innerRadius(R).outerRadius(R + 20);

  // Ribbons stop short of the arc (RIBBON_GAP) and end in an arrowhead
  // pointing at the target group, so the ribbon visibly doesn't touch the
  // border it's flowing into.
  const pointOn = (angle, radius) => [radius * Math.sin(angle), -radius * Math.cos(angle)];

  const RIBBON_GAP = 10;
  const ribbon = d3.ribbonArrow()
    .sourceRadius(R)
    .targetRadius(R - RIBBON_GAP)
    .headRadius(6)
    .padAngle(0.01);

  const svg = d3.select(svgEl)
    .attr('viewBox', `0 0 ${W} ${H}`)
    .append('g')
    .attr('transform', `translate(${W / 2},${H / 2})`);

  // ── Groups (outer arcs) ────────────────────────────────────────────────────
  const group = svg.append('g')
    .selectAll('g')
    .data(chords.groups)
    .join('g');

  group.append('path')
    .attr('class', 'chord-arc')
    .attr('d', arc)
    .attr('fill', d => colorScale(d.index))
    .attr('stroke', '#fff')
    .attr('stroke-width', 0.5)
    .on('mouseenter', function(event, d) {
      const name = names[d.index];
      // directed[i][i] already carries self-trade (see prepareFlowData), and
      // it's already part of both this row's sum and this column's sum, so
      // it must NOT be added a second time here.
      const exported = exportsOf(d.index);
      const imported = importsOf(d.index);
      const unitSuffix = units ? ` ${units}` : '';
      if (change) {
        const p = change.nodePrev[d.index];
        showTip(event, `<strong>${name}</strong><br/><em>Exports</em>${changeTipLines(p.exports, exported, units)}`
          + `<br/><em>Imports</em>${changeTipLines(p.imports, imported, units)}`);
        return;
      }
      const selfLine = selfTotal[d.index] > 0
        ? `<br/>Internal (within ${name}): ${selfTotal[d.index].toLocaleString('en', { maximumFractionDigits: 1 })}${unitSuffix}`
        : '';
      showTip(event, `<strong>${name}</strong><br/>Exports: ${exported.toLocaleString('en', { maximumFractionDigits: 1 })}${unitSuffix}<br/>Imports: ${imported.toLocaleString('en', { maximumFractionDigits: 1 })}${unitSuffix}${selfLine}`);
      // Side by side: light this node's trade in both years' charts
      if (opts.svgEl) spotlightNode(name);
    })
    .on('mousemove', moveTip)
    .on('mouseleave', () => { hideTip(); if (opts.svgEl) clearSpotlight(); });

  // Group label — set on an arc path just outside the ring and attached via
  // <textPath>, so the text bends along the circle. The path itself is always
  // drawn start→end with a consistent sweep (never reversed), which was the
  // source of the earlier mirrored/inward-facing labels on wide or
  // boundary-straddling arcs. Instead, flipping right-side-up on the bottom
  // half is done with the textPath `side` attribute, which mirrors the text
  // to the other side of the same path without touching the path geometry.
  function labelArcPath(d) {
    const rL = R + 24;
    const p0 = pointOn(d.startAngle, rL);
    const p1 = pointOn(d.endAngle, rL);
    const largeArc = (d.endAngle - d.startAngle) > Math.PI ? 1 : 0;
    return `M${p0} A${rL},${rL} 0 ${largeArc} 1 ${p1}`;
  }

  const defs = svg.append('defs');
  defs.selectAll('path')
    .data(chords.groups)
    .join('path')
    .attr('id', (d, i) => `trade-flow-label-arc-${uid}-${i}`)
    .attr('d', labelArcPath);

  const curvedLabels = group.append('text')
    .attr('font-size', 11)
    .attr('font-family', "'GilroyMedium', system-ui, sans-serif").attr('font-weight', 500)
    .attr('fill', '#000')
    .style('pointer-events', 'none');

  curvedLabels.append('textPath')
    .attr('href', (d, i) => `#trade-flow-label-arc-${uid}-${i}`)
    .attr('startOffset', '50%')
    .attr('text-anchor', 'middle')
    .attr('side', d => ((d.startAngle + d.endAngle) / 2) > Math.PI ? 'right' : 'left')
    .text(d => names[d.index]);

  // A dominant flow (common in linear/un-logged mode) can squeeze a neighbour's
  // arc down to almost nothing, so its curved label overflows into adjacent
  // segments and becomes illegible. Measure each label's actual rendered length
  // against the arc it's drawn on; anything that overflows gets swapped for a
  // straight leader line out to a larger radius, where the full name is drawn
  // horizontally with room to breathe instead of being forced onto a tiny curve.
  const LABEL_R  = R + 24;
  const LEADER_R = R + 46;
  const leaders = [];
  curvedLabels.each(function(d) {
    const arcLen  = (d.endAngle - d.startAngle) * LABEL_R;
    const textLen = this.getComputedTextLength ? this.getComputedTextLength() : 0;
    if (textLen <= arcLen * 0.92) return; // fits on its own arc, keep the curved label

    d3.select(this).remove();
    const mid = (d.startAngle + d.endAngle) / 2;
    leaders.push({ index: d.index, mid, right: Math.sin(mid) >= 0, y: pointOn(mid, LEADER_R)[1] });
  });

  // Several tiny neighbouring arcs would otherwise put their leader labels on
  // top of each other. Per side, order labels by their arc's position around
  // the ring (clockwise top→bottom on the right, anticlockwise top→bottom on
  // the left) - NOT by starting height, which near the bottom of the ring
  // disagrees with arc order and makes leader lines cross. Then push labels
  // apart to at least LINE_H (downwards first, then back up if the stack runs
  // off the bottom), sit each just outside the ring at its new height, and
  // bend its leader line to it - the usual donut-chart label layout.
  const LINE_H = 13;
  const Y_MAX = H / 2 - 8;
  [true, false].forEach(right => {
    const side = leaders.filter(l => l.right === right).sort((a, b) => right ? a.mid - b.mid : b.mid - a.mid);
    for (let i = 1; i < side.length; i++) side[i].y = Math.max(side[i].y, side[i - 1].y + LINE_H);
    if (side.length && side[side.length - 1].y > Y_MAX) {
      side[side.length - 1].y = Y_MAX;
      for (let i = side.length - 2; i >= 0; i--) side[i].y = Math.min(side[i].y, side[i + 1].y - LINE_H);
    }
  });

  leaders.forEach(l => {
    const sign = l.right ? 1 : -1;
    const p0 = pointOn(l.mid, R + 20);
    const elbow = pointOn(l.mid, R + 30);
    // Hug the ring, but never sit inward of the label's own elbow: near the
    // bottom, labels pushed down would otherwise land inward of their arcs,
    // and a line running down-and-inward to a higher label crosses one from
    // a more central arc running to a lower label. Keeping every line heading
    // outward (with labels already in arc order) means neighbours can't cross.
    const hugX = Math.sqrt(Math.max(0, LEADER_R * LEADER_R - l.y * l.y)) + 6;
    const x = sign * Math.max(hugX, Math.abs(elbow[0]) + 12);

    svg.append('polyline')
      .attr('points', `${p0} ${elbow} ${x - sign * 3},${l.y}`)
      .attr('fill', 'none')
      .attr('stroke', '#999')
      .attr('stroke-width', 0.75)
      .style('pointer-events', 'none');

    svg.append('text')
      .attr('x', x)
      .attr('y', l.y)
      .attr('dy', '0.35em')
      .attr('text-anchor', l.right ? 'start' : 'end')
      .attr('font-size', 11)
      .attr('font-family', "'GilroyMedium', system-ui, sans-serif").attr('font-weight', 500)
      .attr('fill', '#000')
      .style('pointer-events', 'none')
      .text(names[l.index]);
  });

  // ── Ribbons ────────────────────────────────────────────────────────────────
  // One tapered, arrow-headed ribbon per direction (A→B and B→A are drawn
  // separately), each ending short of the destination arc so the arrowhead
  // is visibly detached from the border it points into. Self-chords (internal
  // trade, source.index === target.index) are excluded here and drawn
  // separately below — the generic ribbon generator connects two thin
  // sub-slices wherever d3-chord happened to place them internally, which
  // doesn't read as the same "region trading with itself" shape the old
  // fixed-width loop had.
  svg.append('g')
    .attr('fill-opacity', 0.65)
    .selectAll('path')
    .data(chords.filter(d => d.source.index !== d.target.index))
    .join('path')
    .attr('class', 'chord-ribbon')
    .attr('d', ribbon)
    .attr('fill', d => flowColor(d.source.index, d.target.index))
    .attr('stroke', d => d3.rgb(flowColor(d.source.index, d.target.index)).darker())
    .attr('stroke-width', 0.3)
    .style('cursor', 'pointer')
    .on('mouseenter', function(event, d) {
      d3.select(this).attr('fill-opacity', 1);
      const i = d.source.index, j = d.target.index;
      const head = `<strong>${names[i]} → ${names[j]}</strong>`;
      if (change) { showTip(event, head + changeTipLines(change.flowPrev(i, j), directed[i][j], units)); return; }
      const fmt = v => v.toLocaleString('en', { maximumFractionDigits: 1 });
      showTip(event, `${head}<br/>${fmt(directed[i][j])}${units ? ' ' + units : ''}${unitValueLine(unitValueOf(i, j))}`);
    })
    .on('mousemove', moveTip)
    .on('mouseleave', function() {
      d3.select(this).attr('fill-opacity', 0.65);
      hideTip();
    });

  // ── Self-loops (internal trade) ─────────────────────────────────────────────
  // d3.chordDirected() already gave this its own correctly-sized, non-
  // overlapping pair of sub-slices within the group's angle (source = the
  // "outgoing to self" half, target = the "incoming from self" half — see
  // prepareFlowData's diagonal comment). Outgoing sub-slices are sorted
  // before incoming ones within a group, so in practice these two halves
  // land close to the two ends of the group's own arc, same as the loop
  // used to be drawn explicitly - the difference is this position is now
  // data-driven and guaranteed not to collide with anything else, rather
  // than a fixed decorative slice carved out of already-claimed space.
  const ARROW_HEAD_RADIUS = 6; // matches the cross-node ribbons' .headRadius(6)
  function selfLoopPath(d) {
    const importR = R - RIBBON_GAP;
    const headR = importR - ARROW_HEAD_RADIUS;
    const p0a = pointOn(d.source.startAngle, R);
    const p0b = pointOn(d.source.endAngle, R);
    // The originating edge sits at a constant radius (R) across its whole
    // angular span, so it needs to follow the circle's own curvature (an
    // SVG arc) rather than a straight line between its two endpoints — a
    // straight chord reads as a visibly flat edge once the span is wide
    // enough (data-driven self-trade can easily be wider than the old fixed
    // decorative slice was).
    const sourceSpan = d.source.endAngle - d.source.startAngle;
    const largeArc = sourceSpan > Math.PI ? 1 : 0;
    const mid = (d.target.startAngle + d.target.endAngle) / 2;
    const baseStart = pointOn(d.target.startAngle, headR);
    const apex = pointOn(mid, importR);
    const baseEnd = pointOn(d.target.endAngle, headR);
    return `M${p0a} A${R},${R} 0 ${largeArc} 1 ${p0b} Q0,0 ${baseStart} L${apex} L${baseEnd} Q0,0 ${p0a} Z`;
  }
  svg.append('g')
    .attr('fill-opacity', 0.65)
    .selectAll('path')
    .data(chords.filter(d => d.source.index === d.target.index))
    .join('path')
    .attr('class', 'chord-ribbon')
    .attr('d', selfLoopPath)
    .attr('fill', d => flowColor(d.source.index, d.source.index))
    .attr('stroke', d => d3.rgb(flowColor(d.source.index, d.source.index)).darker())
    .attr('stroke-width', 0.3)
    .style('cursor', 'pointer')
    .on('mouseenter', function(event, d) {
      d3.select(this).attr('fill-opacity', 1);
      const i = d.source.index;
      const head = `<strong>${names[i]} (internal)</strong>`;
      if (change) { showTip(event, head + changeTipLines(change.flowPrev(i, i), directed[i][i], units)); return; }
      const fmt = v => v.toLocaleString('en', { maximumFractionDigits: 1 });
      showTip(event, `${head}<br/>${fmt(directed[i][i])}${units ? ' ' + units : ''}${unitValueLine(unitValueOf(i, i))}`);
    })
    .on('mousemove', moveTip)
    .on('mouseleave', function() {
      d3.select(this).attr('fill-opacity', 0.65);
      hideTip();
    });

  applyChordFocus(svgEl);
}

// ── Build Sankey diagram ────────────────────────────────────────────────────
// Same region/country nodes as the chord (via prepareFlowData), laid out as
// exactly two columns: exporters on the left, importers on the right. Every
// node can appear on both sides, so a selected region/country shows its
// exports (left → partners) and its imports (partners → right) together.
// Because every link runs left → right between separate exporter/importer
// nodes, the graph is always acyclic - reciprocal trade (A → B and B → A) and
// internal trade (A → A) are just ordinary links.
// Side by side passes `opts`: the target svgEl, its prepared data, the shared
// colorMap, and maxScale (cap on pixels per unit of trade, so both years'
// charts share one scale). Returns the pixels-per-unit scale it drew at.
function buildSankey(year, baseYear = null, opts = {}) {
  const svgEl = opts.svgEl ?? svgRef.value;
  if (!svgEl) return 0;
  // Clearing children isn't enough — the chord view sets a viewBox attribute
  // directly on svgEl, which persists across views (it's an attribute, not a
  // child) and would otherwise distort this view's own coordinate space.
  d3.select(svgEl).selectAll('*').remove();
  removeMapZoom(svgEl);
  svgEl.removeAttribute('viewBox');

  const data = 'data' in opts ? opts.data : prepareFlowData(year, baseYear);
  if (!data) {
    chordNodes.value = [];
    if (opts.svgEl) drawEmptyNote(svgEl, year);
    return 0;
  }
  const { names, directed, units, unitValueOf, change } = data;
  compareSummary.value = change?.summary ?? null;

  const colorMap = opts.colorMap ?? sankeyColorMap(data);
  chordNodes.value = names.map(name => ({ name, color: colorMap.get(name) ?? NO_COLOR }));
  const colorOf = (name) => colorMap.get(name) ?? NO_COLOR;

  const expId = (name) => `exp:${name}`;
  const impId = (name) => `imp:${name}`;
  // Always linear: ribbon widths are true proportions of trade volume
  const valueOf = f => Math.max(f, 0.0001);
  const links = [];
  for (let i = 0; i < names.length; i++) {
    for (let j = 0; j < names.length; j++) {
      const flow = directed[i][j];
      if (flow > 0) links.push({ source: expId(names[i]), target: impId(names[j]), value: valueOf(flow), flow, uv: unitValueOf(i, j), from: names[i], to: names[j], prev: change?.flowPrev(i, j) ?? 0 });
    }
  }
  if (!links.length) { chordNodes.value = []; return 0; }

  // Only nodes that actually export (left) / import (right) something.
  const order = new Map(names.map((n, i) => [n, i]));
  const exporters = names.filter(n => links.some(l => l.from === n));
  const importers = names.filter(n => links.some(l => l.to === n));
  const nodes = [
    ...exporters.map(name => ({ id: expId(name), name, side: 'export' })),
    ...importers.map(name => ({ id: impId(name), name, side: 'import' })),
  ];

  // Focus: the selection highlight (if any) fades everything that isn't the
  // selection's trade.
  const hl = data.highlight;
  const isDimmed = (name) => hl ? !hl.nodes.has(name) : false;
  const isLinkDimmed = (from, to) => hl ? !hl.isFlow(from, to) : false;

  const W = svgEl.clientWidth  || 900;
  const H = svgEl.clientHeight || 560;
  // PAD_H reserves room for the column labels, which sit OUTSIDE the node bar
  // (text-anchor end/start with an 8px offset) - too small a margin clips a
  // name like "North America" against the container's overflow:hidden.
  // PAD_TOP leaves room for the "Exporters" / "Importers" column headings.
  const PAD_TOP = 40, PAD_BOTTOM = 24, PAD_H = 130, NODE_W = 14;

  const root = d3.select(svgEl);
  const g = root.append('g');

  // Keep both columns in the same (region-grouped) order as the chord, so a
  // region sits at roughly the same height on each side.
  const graph = d3Sankey()
    .nodeId(d => d.id)
    .nodeWidth(NODE_W)
    .nodePadding(10)
    .nodeAlign(node => node.side === 'export' ? 0 : 1)
    .nodeSort((a, b) => order.get(a.name) - order.get(b.name))
    .extent([[PAD_H, PAD_TOP], [W - PAD_H, H - PAD_BOTTOM]])
    ({ nodes, links: links.map(l => ({ ...l })) });

  // d3-sankey scales so the busiest column fills the whole height - when one
  // hub node (e.g. Asia) takes in nearly everything, that node alone spans
  // top to bottom. Cap any single node at MAX_NODE_FRAC of the plot height by
  // shrinking every node (about its own centre) and ribbon by the same factor,
  // so relative widths stay correct; update() re-threads the ribbons.
  // The same shrink applies opts.maxScale (side by side's shared scale).
  const MAX_NODE_FRAC = 0.6;
  const plotH = H - PAD_TOP - PAD_BOTTOM;
  const tallestNode = d3.greatest(graph.nodes, n => n.y1 - n.y0);
  const tallest = tallestNode ? tallestNode.y1 - tallestNode.y0 : 0;
  const naturalScale = tallestNode?.value ? tallest / tallestNode.value : 0; // px per unit as laid out
  let s = tallest > plotH * MAX_NODE_FRAC ? (plotH * MAX_NODE_FRAC) / tallest : 1;
  if (opts.maxScale && naturalScale * s > opts.maxScale) s = opts.maxScale / naturalScale;
  if (s < 1) {
    graph.nodes.forEach(n => {
      const mid = (n.y0 + n.y1) / 2, half = (n.y1 - n.y0) * s / 2;
      n.y0 = mid - half; n.y1 = mid + half;
    });
    graph.links.forEach(l => { l.width *= s; });
    d3Sankey().update(graph);
  }

  const fmt = v => v.toLocaleString('en', { maximumFractionDigits: 1 });
  const unitSuffix = units ? ` ${units}` : '';
  const linkPath = sankeyLinkHorizontal();

  // Raw (non-log) totals per node, for the node tooltips.
  const nodeFlow = new Map();
  graph.links.forEach(l => {
    nodeFlow.set(l.source.id, (nodeFlow.get(l.source.id) || 0) + l.flow);
    nodeFlow.set(l.target.id, (nodeFlow.get(l.target.id) || 0) + l.flow);
  });

  // Flat, translucent ribbons in the exporter's colour (no gradient) — softer
  // and wider-reading than a stroke gradient, closer to how published flow
  // diagrams render them.
  // Change view: ribbons are coloured by their flow's change, and a bit more
  // opaque so the decline ↔ growth tint reads clearly.
  const baseLinkOpacity = change ? 0.55 : 0.35;
  graph.links.forEach(l => {
    const dimmed = isLinkDimmed(l.from, l.to);
    const label = l.from === l.to ? `${l.from} (internal)` : `${l.from} → ${l.to}`;
    l.baseOpacity = dimmed ? 0.1 : baseLinkOpacity; // restored after a movers-panel spotlight
    g.append('path')
      .datum(l)
      .attr('class', 'sankey-link')
      .attr('d', linkPath(l))
      .attr('fill', 'none')
      .attr('stroke', change ? changeColor(l.prev, l.flow) : colorOf(l.from))
      .attr('stroke-width', Math.max(2, l.width))
      .attr('stroke-opacity', l.baseOpacity)
      .on('mouseenter', function(ev) {
        if (!dimmed) d3.select(this).attr('stroke-opacity', 0.85);
        const body = change ? changeTipLines(l.prev, l.flow, units) : `<br/>${fmt(l.flow)}${unitSuffix}${unitValueLine(l.uv)}`;
        showTip(ev, `<strong>${label}</strong>${body}`);
      })
      .on('mousemove', moveTip)
      .on('mouseleave', function() { d3.select(this).attr('stroke-opacity', l.baseOpacity); hideTip(); });
  });

  const labelFont = "'GilroyMedium', system-ui, sans-serif";
  graph.nodes.forEach(node => {
    const dimmed = isDimmed(node.name);
    const isExporter = node.side === 'export';
    const sideLabel = isExporter ? 'Exports' : 'Imports';
    const curr = nodeFlow.get(node.id) || 0;
    const prevOfSide = change ? change.nodePrev[names.indexOf(node.name)][isExporter ? 'exports' : 'imports'] : 0;
    const tip = change
      ? `<strong>${node.name}</strong><br/><em>${sideLabel}</em>${changeTipLines(prevOfSide, curr, units)}`
      : `<strong>${node.name}</strong><br/>${sideLabel}: ${fmt(curr)}${unitSuffix}`;
    g.append('rect')
      .attr('x', node.x0).attr('y', node.y0)
      .attr('width', node.x1 - node.x0).attr('height', Math.max(2, node.y1 - node.y0))
      .attr('fill', change ? changeColor(prevOfSide, curr) : colorOf(node.name)).attr('rx', 2).attr('fill-opacity', dimmed ? 0.25 : 1)
      .on('mouseenter', (ev) => {
        showTip(ev, tip);
        // Side by side: light this node's trade in both years' charts
        if (opts.svgEl) spotlightNode(node.name);
      })
      .on('mousemove', moveTip)
      .on('mouseleave', () => { hideTip(); if (opts.svgEl) clearSpotlight(); });

    g.append('text')
      .attr('x', isExporter ? node.x0 - 8 : node.x1 + 8)
      .attr('y', (node.y0 + node.y1) / 2)
      .attr('text-anchor', isExporter ? 'end' : 'start')
      .attr('dominant-baseline', 'middle')
      // Same label style as the chord view
      .attr('font-size', 11)
      .attr('font-family', labelFont).attr('font-weight', 500)
      .attr('fill', dimmed ? '#c0c8d0' : '#000')
      .text(node.name);
  });

  // Column headings
  [['Exporters', PAD_H + NODE_W / 2], ['Importers', W - PAD_H - NODE_W / 2]].forEach(([text, x]) => {
    g.append('text')
      .attr('x', x)
      .attr('y', PAD_TOP - 16)
      .attr('text-anchor', 'middle')
      .attr('font-size', 12)
      .attr('font-family', labelFont).attr('font-weight', 600)
      .attr('fill', '#000')
      .text(text);
  });

  return naturalScale * s;
}

// Sankey colours by the Sankey's own bar sizes: a name shows up as separate
// exporter and importer bars, so the chord's combined-throughput rank can put
// the primary colour on a node that isn't the tallest bar. Rank by each
// name's bigger side instead, so the biggest bar always gets the primary
// brand colour. Returns name → colour.
function sankeyColorMap({ names, directed }) {
  const barSize = names.map((_, i) => Math.max(
    directed[i].reduce((s, v) => s + v, 0),
    directed.reduce((s, row) => s + row[i], 0),
  ));
  const ranked = [...names.keys()].sort((a, b) => barSize[b] - barSize[a]);
  return new Map(ranked.map((i, rank) => [names[i], BRAND_PALETTE[rank % BRAND_PALETTE.length]]));
}

// ── Build world map view ─────────────────────────────────────────────────────
// Unlike the chord, the map always plots individual countries directly - a
// region isn't a single point, so region-node rollup (buildChord's `nodeOf`)
// doesn't apply here, and nor does Show rest of world's single Rest of World
// node (it behaves like Show other regions). The region/country selection
// still narrows (or, with other trade shown, highlights) which flows are in
// play, just without collapsing the survivors into one region node.

// Flow thickness slider: multiplies the map's arc widths. Applied in place, so
// dragging it keeps the current zoom/pan; buildMap passes it on redraws.
const mapArcScale = ref(1);
const arcWidthOpen = ref(false); // slider slid out from its corner button
function onMapArcScale(scale) {
  mapArcScale.value = scale;
  setFlowMapWidthScale(svgRef.value, scale);
}

async function buildMap(year, baseYear = null) {
  const svgEl = svgRef.value;
  if (!svgEl) return;
  // See buildSankey's comment — the chord view's viewBox attribute persists
  // across views unless explicitly cleared.
  d3.select(svgEl).selectAll('*').remove();
  svgEl.removeAttribute('viewBox');

  // Manual picks take over from Top N while any are set.
  const manualActive = effectiveFilter.value.length > 0;
  const top = manualActive ? topNCountries(year, { off: true }) : topNCountries(year);

  const flowMap = new Map(); // 'src|||dst' -> volume
  const prevFlowMap = new Map(); // like flowMap, for baseYear (Change view)
  const uvOfRow = unitValueLookup(year);
  const flowUv = unitValueAccumulator(); // keyed like flowMap
  for (const row of rawRows.value) {
    const src = row.country, dst = row.tradePartner;
    if (!src || !dst || src === dst) continue;
    if (!(row.typeName || '').toLowerCase().includes('export')) continue;
    const val = Number(row.yearlyData?.[year] ?? 0);
    const prev = baseYear ? Number(row.yearlyData?.[baseYear] ?? 0) : 0;
    if (!val && !prev) continue;
    if (!showOthers.value) {
      if (manualActive && !isPickedCountry(src) && !isPickedCountry(dst)) continue;
      if (!top.passes(src, dst)) continue;
    }
    const key = `${src}|||${dst}`;
    if (prev) prevFlowMap.set(key, (prevFlowMap.get(key) || 0) + prev);
    if (!val) continue;
    flowMap.set(key, (flowMap.get(key) || 0) + val);
    flowUv.add(key, uvOfRow(row), val);
  }

  if (!flowMap.size) { chordNodes.value = []; return; }

  const units = rawRows.value.find(r => r.units)?.units || '';
  const entries = [...flowMap.entries()].map(([key, flow]) => {
    const [from, to] = key.split('|||');
    return { from, to, flow, units, uv: flowUv.get(key), prev: prevFlowMap.get(key) || 0 };
  });

  // Change view: country-level movers/totals (the map has no region nodes),
  // and each country dot coloured by its total trade change.
  let countryChangeColor = null;
  if (baseYear) {
    const flows = [...new Set([...flowMap.keys(), ...prevFlowMap.keys()])].map(key => {
      const [from, to] = key.split('|||');
      return { from, to, prev: prevFlowMap.get(key) || 0, curr: flowMap.get(key) || 0 };
    });
    const tally = new Map(); // country → { prev, curr }
    flows.forEach(f => [f.from, f.to].forEach(name => {
      const t = tally.get(name) || { prev: 0, curr: 0 };
      t.prev += f.prev; t.curr += f.curr;
      tally.set(name, t);
    }));
    countryChangeColor = (name) => { const t = tally.get(name); return t ? changeColor(t.prev, t.curr) : CHANGE_COLORS.flat; };
    compareSummary.value = {
      baseYear, year, units,
      totals: { prev: d3.sum(flows, f => f.prev), curr: d3.sum(flows, f => f.curr) },
      movers: rankMovers(flows),
    };
  }

  const names = [...new Set(entries.flatMap(e => [e.from, e.to]))].sort();
  // Colours by volume (same as the chord/Sankey): the country with the most
  // trade gets the primary brand colour, and so on down the palette.
  const countryTotal = new Map();
  entries.forEach(e => {
    countryTotal.set(e.from, (countryTotal.get(e.from) || 0) + e.flow);
    countryTotal.set(e.to, (countryTotal.get(e.to) || 0) + e.flow);
  });
  const colorRank = new Map([...names].sort((a, b) => countryTotal.get(b) - countryTotal.get(a)).map((name, rank) => [name, rank]));
  chordNodes.value = names.map(name => ({ name, color: BRAND_PALETTE[colorRank.get(name) % BRAND_PALETTE.length] }));
  const colorMap = Object.fromEntries(chordNodes.value.map(n => [n.name, n.color]));
  const colorOf = (name) => colorMap[name] ?? '#9dabaf';

  const world = await loadWorldMap();
  // A redraw may have been requested (filter change, view switch) while the
  // map data was loading - bail if this call is no longer the latest.
  if (chartView.value !== 'map' || svgRef.value !== svgEl) return;

  // Focus, with the rest of world shown: the selected countries (manual picks
  // or Top N) and their flows stay bright and everything else fades.
  const selectionHighlight = focusSelection.value && showOthers.value && (manualActive || top.active);
  const isSelected = (name) => manualActive ? isPickedCountry(name) : top.all.has(name);
  const fmt = v => v.toLocaleString('en', { maximumFractionDigits: 1 });
  drawFlowMap(svgEl, world, entries, {
    centerName: null,
    widthScale: mapArcScale.value,
    colorOf: countryChangeColor ? (name, link) => changeColor(link.prev, link.flow) : colorOf,
    nodeColorOf: countryChangeColor ?? colorOf,
    isDimmed: name => selectionHighlight && !isSelected(name),
    onHover: (ev, link) => {
      const body = countryChangeColor
        ? changeTipLines(link.prev, link.flow, link.units)
        : `<br/>${fmt(link.flow)}${link.units ? ' ' + link.units : ''}${unitValueLine(link.uv)}`;
      showTip(ev, `<strong>${link.from} → ${link.to}</strong>${body}`);
    },
    // Country pin: its trades among the flows currently drawn (i.e. filtered)
    onPinHover: (ev, summary) => {
      const { title, lines } = pinTooltipLines(summary);
      showTip(ev, `<strong>${title}</strong>` + lines.map(l =>
        `<br/>${l.sub ? '<span style="opacity:0.75;padding-left:8px;">' + l.text + '</span>' : l.text}`).join(''));
    },
    onMove: moveTip,
    onLeave: hideTip,
  });
}

// Chord view Focus: fades arcs/ribbons outside the selection highlight, or
// clears fading when there's none. The highlight (manual picks or Top N, see
// prepareFlowData; null when Focus is off or there's no selection) and node
// names are stored on each chord's SVG by buildChord. (Sankey and map bake
// their fading in at build time instead.)
function applyChordFocus(svgEl) {
  if (!svgEl?.__chord) return;
  const { names, highlight: hl } = svgEl.__chord;
  if (!hl) {
    d3.select(svgEl).selectAll('.chord-arc, .chord-ribbon').attr('opacity', 1);
    return;
  }
  d3.select(svgEl).selectAll('.chord-arc').attr('opacity', d => hl.nodes.has(names[d.index]) ? 1 : 0.35);
  d3.select(svgEl).selectAll('.chord-ribbon').attr('opacity', d =>
    hl.isFlow(names[d.source?.index], names[d.target?.index]) ? 1 : 0.12);
}

// ── Tooltip ───────────────────────────────────────────────────────────────────
function showTip(event, html) {
  const el = tooltipEl.value;
  if (!el) return;
  el.innerHTML = html;
  el.style.display = 'block';
  moveTip(event);
}
function moveTip(event) {
  const el = tooltipEl.value;
  if (!el) return;
  const wrap = captureDiv.value?.getBoundingClientRect();
  const OFFSET = 10, EDGE = 4;
  const cx = event.clientX - (wrap?.left ?? 0);
  const cy = event.clientY - (wrap?.top  ?? 0);
  // The chart box clips its overflow, so flip the tooltip to the other side
  // of the cursor when it would run past the right/bottom edge.
  const w = el.offsetWidth, h = el.offsetHeight;
  let x = cx + OFFSET, y = cy + OFFSET;
  if (wrap && x + w > wrap.width - EDGE)  x = cx - OFFSET - w;
  if (wrap && y + h > wrap.height - EDGE) y = cy - OFFSET - h;
  el.style.left = `${Math.max(EDGE, x)}px`;
  el.style.top  = `${Math.max(EDGE, y)}px`;
}
function hideTip() {
  if (tooltipEl.value) tooltipEl.value.style.display = 'none';
}

// ── Export ────────────────────────────────────────────────────────────────────
const exportDiv = ref(null);
const updatedDate = ref(new Date());

function exportChartAs(type) {
  const el = exportDiv.value;
  if (!el) return;

  // Track Trade Flow graphic export
  logUserAction(
    USER_ACTIONS.DOWNLOAD_TRADE_FLOW_GRAPH,
    `Exported Trade Flow graphic as ${type.toUpperCase()} (Commodity: "${FILTERS.ELEMENT || 'N/A'}", HS Code: "${FILTERS.PRODUCT || 'N/A'}")`
  );

  const fn  = type === 'svg' ? htmlToImage.toSvg : htmlToImage.toPng;
  const ext = type === 'svg' ? 'svg' : 'png';
  // Keep the filters drawer (teleported into the chart box) and on-chart
  // controls out of the image
  const filter = node => !node.classList?.contains('n-drawer-container') && !node.classList?.contains('tf-no-export');
  fn(el, { filter }).then(dataUrl => {
    const link = document.createElement('a');
    link.download = `trade-flow-chord.${ext}`;
    link.href = dataUrl;
    link.click();
  });
}

// ── Auto-select first element when subscription data loads ────────────────────
watch(
  () => SUBSCRIPTION_INFO?.dataElements,
  (newVal) => {
    if (newVal?.length && !FILTERS.ELEMENT) {
      FILTERS.ELEMENT = newVal[0].code;
      onElementChange(newVal[0].code);
    }
  },
  { immediate: true }
);

// ── Click-outside to close export menu ────────────────────────────────────────
// (The filters NDrawer handles its own mask-click/ESC dismissal.)
function handleClickOutside(e) {
  if (chartMenuOpen.value && !e.target.closest('.relative.inline-block')) {
    chartMenuOpen.value = false;
  }
}
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  getCountryRegionMap().then(map => { countryRegionMap.value = map; });
});
onBeforeUnmount(() => document.removeEventListener('click', handleClickOutside));

// ── Helpers ───────────────────────────────────────────────────────────────────
function clearChart() {
  chordNodes.value = [];
  rawRows.value = [];
  unitValueRows.value = [];
  compareSummary.value = null;
  splitTotals.value = null;
  [svgRef.value, svgBaseRef.value].forEach(el => { if (el) d3.select(el).selectAll('*').remove(); });
}
</script>

<style lang="scss" scoped>
.tf-toolbar {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 36px;
  padding: 0 0.5rem;
  background: #f2f2f2;
  border-radius: 6px 6px 0 0;
}

.tf-view-toggle {
  display: flex;
  gap: 4px;
  align-items: center;
}
.tf-view-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: none;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  outline: none;
  background: transparent;
  color: #000;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  font-family: 'GilroyMedium', system-ui, sans-serif;
  &:hover { color: #213CE5; }
  &.active {
    color: #213CE5 !important;
    border: none !important;
    border-bottom: 2px solid #213CE5 !important;
    background-color: transparent !important;
  }
}

.chord-layout {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  padding: 8px 0;
}

.chord-export-area {
  flex: 1;
  min-width: 0;
  background: #fff;
}

.chord-export-source {
  padding: 8px 20px 4px;
}

.chord-canvas-wrap {
  position: relative;
  height: 560px;
  // Project Blue watermark, faded with the same 80% white overlay the other
  // market data charts use
  background:
    linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)),
    url('https://driqq8crwypb5.cloudfront.net/articles/projectblue-data-dash-image.png') center / 60% no-repeat,
    #fff;
  border: 1px solid #e8ecff;
  // Square corners - the filters drawer is clipped to this box, so a radius
  // here would round the drawer's outer corners too
  border-radius: 0;
  overflow: hidden;
  :deep(.n-drawer) { border-radius: 0 !important; }
  // Drawer close button. naive-ui sizes the button to the icon and draws the
  // hover box as a larger, separately-centred ::before - but a global button
  // style resizes/pads the button, so the X and the hover box no longer share
  // a centre. Pin the button to the hover box's size and fill it with the
  // ::before, so both are centred on the same square.
  :deep(.n-drawer-header__close) {
    box-sizing: border-box !important;
    width: 24px !important;
    height: 24px !important;
    min-width: 0 !important;
    min-height: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 !important;
    margin: 0 !important;
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    line-height: 0 !important;
    &::before {
      inset: 0 !important;
      width: auto !important;
      height: auto !important;
      transform: none !important;
    }
    .n-base-icon {
      display: flex !important;
      position: relative; // above the ::before hover box
    }
    svg { display: block; }
  }
  // NDrawer's mask/panel are position:fixed internally, which normally means
  // "fixed to the viewport" regardless of where in the DOM they're teleported
  // to. `transform` here establishes this element as their containing block
  // instead (a standard CSS behaviour), so the drawer teleported into it (see
  // the filters NDrawer's `:to="captureDiv"`) is genuinely confined to this
  // box instead of covering the whole page.
  transform: translateZ(0);
}

.chord-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.chord-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  color: #9dabaf;
  font-size: 13px;
}

.chord-tooltip {
  position: absolute;
  background: rgba(20, 30, 60, 0.88);
  color: #fff;
  font-size: 12px;
  font-family: 'GilroyMedium', system-ui, sans-serif;
  padding: 7px 12px;
  border-radius: 6px;
  pointer-events: none;
  line-height: 1.5;
  max-width: 240px;
  z-index: 20;
}


.tf-rail-btn {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 8px 10px;
  border: 1px solid #e0e3ef;
  border-radius: 3px;
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(6px);
  color: #000;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  font-family: 'GilroyMedium', system-ui, sans-serif;
  &:hover { border-color: #213CE5; color: #213CE5; }
  &.active { border-color: #213CE5; color: #213CE5; background: rgba(33,60,229,0.06); }
}
.tf-rail-badge {
  position: absolute;
  top: -5px; right: -5px;
  background: #213CE5;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  border-radius: 8px;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  line-height: 1;
  font-family: 'GilroyMedium', system-ui, sans-serif;
  font-variant-numeric: tabular-nums;
}
// Gilroy's line box has more space below the glyphs than above, so the digit
// sits high when the box is centred. Trimming the number's box to the cap
// height and baseline makes it exactly the digit's height, so the badge's flex
// centring is true. (text-box only applies to block/inline boxes, hence the
// inner span rather than setting it on the flex badge itself.)
.tf-rail-badge__num {
  display: block;
  text-box: trim-both cap alphabetic;
  @supports not (text-box: trim-both cap alphabetic) {
    padding-top: 1px; // older browsers: nudge the digit down to optical centre
  }
}

// Export and import share one drawer, stacked as two sections that split the
// available height evenly - each section's own country list scrolls inside
// its half instead of the whole drawer scrolling as one long list.
.tf-drawer-body {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.tf-drawer-section {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.tf-drawer-header {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px !important;
  color: #000 !important;
}
// Collapsed sections shrink to their header so the open one takes the height.
// Short sections (e.g. Top N) size to their content instead of sharing the
// drawer's spare height with the scrolling country list.
.tf-drawer-section--fit {
  flex: 0 0 auto;
  .tf-chip-row { margin-bottom: 8px; }
}
.tf-drawer-section--collapsed {
  flex: 0 0 auto;
  .tf-panel-header { margin-bottom: 0; }
}
.tf-section-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
  color: #000 !important;
  .tf-panel-title { margin-bottom: 0; }
}
.tf-drawer-section .tf-country-list {
  max-height: none;
  flex: 1;
  min-height: 0;
}

.tf-toolbar-centre {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  gap: 6px;
  pointer-events: auto;
  z-index: 10;
}
.tf-rail-btn--inline {
  flex-direction: row !important;
  padding: 4px 10px !important;
  gap: 5px !important;
  font-size: 11px !important;
  background: transparent !important;
  box-shadow: none !important;
  border-color: #c8cfe8 !important;
}
.tf-rail-badge--inline {
  position: static !important;
  top: unset !important;
  right: unset !important;
  margin-left: 2px;
}
.tf-rail-btn--reset {
  color: #c0392b;
  border-color: #f5c6c2;
  &:hover { border-color: #c0392b; background: rgba(192,57,43,0.05); }
}
.tf-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
// Two columns:  [☐ Show other regions ⓘ]  [Top N ⓘ chips]
//               [☐ Show rest of world ⓘ]
.tf-options-row {
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  align-items: center;
  gap: 6px 12px;
  .tf-topn-inline { grid-row: 1; grid-column: 2; }
  margin-bottom: 20px; // spacing in place of a divider before Regions & countries
  .tf-hover-toggle {
    width: auto;
    margin-bottom: 0;
    padding-right: 0;
    white-space: nowrap;
  }
  .tf-hover-toggle:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  .tf-chip { padding: 3px 6px; }
  .tf-chip-row { flex-wrap: nowrap; }
}
.tf-option-label,
.tf-topn-inline {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.tf-topn-inline {
  gap: 6px;
  .tf-panel-title { margin: 0; white-space: nowrap; line-height: 1; }
}
// line-height 0 so the icon's box is exactly the SVG and centres on the text
.tf-info-icon {
  display: inline-flex;
  align-items: center;
  line-height: 0;
  color: #7a8096;
  cursor: help;
  &:hover { color: #213CE5; }
}
// Selected Top N value while it's paused by a manual selection
.tf-chip--paused.active { opacity: 0.5; }
.tf-chip {
  padding: 3px 10px;
  border: 1px solid #e0e3ef;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 400;
  color: #000;
  cursor: pointer;
  transition: all 0.13s;
  background: #fff;
  font-family: 'GilroyMedium', system-ui, sans-serif;
  &:hover { border-color: #213CE5; color: #213CE5; }
  &.active { background: #213CE5; border-color: #213CE5; color: #fff; }
}

.tf-panel-title {
  font-size: 12px;
  font-weight: 600;
  color: #000 !important;
  margin-bottom: 8px;
}
.tf-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
// Title with its ⓘ icon right beside it rather than pushed to the far edge
.tf-panel-header--start {
  justify-content: flex-start;
  gap: 6px;
}
// Focus toggle sits at the right end of the Regions & countries heading
.tf-focus-toggle {
  margin-left: auto;
  .tf-hover-toggle {
    width: auto;
    margin-bottom: 0;
    padding-right: 0;
    white-space: nowrap;
  }
  .tf-hover-toggle:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}
.tf-panel-hint {
  font-size: 12px !important;
  color: #000;
  margin: 0 0 8px;
}
.tf-clear-link {
  border: none;
  background: none;
  color: #9dabaf;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  font-family: 'GilroyMedium', system-ui, sans-serif;
  &:hover { color: #c0392b; }
}
.tf-search-input {
  width: 100%;
  box-sizing: border-box;
  padding: 5px 8px;
  border: 1px solid #e0e3ef;
  border-radius: 0;
  font-size: 12px;
  outline: none;
  margin-bottom: 8px;
  color: #000;
  font-family: 'GilroyMedium', system-ui, sans-serif;
  &::placeholder { color: #b0bec5; }
  &:focus { border-color: #213CE5; }
}
.tf-country-list {
  max-height: 220px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin: 0 -2px;
}
.tf-country-item {
  padding: 5px 8px;
  border-radius: 3px;
  font-size: 12px;
  cursor: pointer;
  color: #000;
  text-align: left;
  background: none;
  border: none;
  transition: background 0.1s;
  font-family: 'GilroyMedium', system-ui, sans-serif;
  &:hover { background: #f2f2f2; }
  &.active {
    background: #eef0ff; color: #213CE5; font-weight: 600;
    // an imported base style puts a full border on .active - strip it
    border: none !important;
    box-shadow: none !important;
  }
}
.tf-country-item--check {
  display: flex;
  align-items: center;
  gap: 7px;
}
.tf-check-box {
  flex-shrink: 0;
  width: 14px; height: 14px;
  border: 1px solid #c8cfe8;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  transition: all 0.12s;
}
.tf-country-item--check.active .tf-check-box,
.tf-check-box--checked,
.tf-check-box--indeterminate {
  background: #213CE5;
  border-color: #213CE5;
}
.tf-country-item--check.active {
  background: none;
  color: #213CE5;
}
.tf-hover-toggle {
  width: 100%;
  margin-bottom: 8px;
  padding-left: 0;
  &.active {
    border: none !important
  }
  &:hover {
    background: #FFF;
  }
}
:deep(.chord-arc),
:deep(.chord-ribbon) {
  transition: opacity 0.15s ease;
}

.tf-region-group {
  margin-bottom: 2px;
}
.tf-region-header {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 5px 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: #000 !important;
  font-family: 'GilroyMedium', system-ui, sans-serif;
  border-radius: 3px;
}
.tf-region-chevron {
  flex-shrink: 0;
  color: #9dabaf;
  transition: transform 0.15s;
  &.tf-region-chevron--open { transform: rotate(90deg); }
}
.tf-region-name {
  flex: 1;
  text-align: left;
}
.tf-region-countries {
  padding-left: 18px;
}

// ── Year comparison ──────────────────────────────────────────────────────────
.tf-compare-toggle {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid #c8cfe8;
  border-radius: 3px;
  background: #fff;
  color: #000;
  font-size: 12px;
  font-family: 'GilroyMedium', system-ui, sans-serif;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  &:hover:not(:disabled) { border-color: #213CE5; color: #213CE5; }
  &.active {
    border-color: #213CE5 !important;
    background: #213CE5 !important;
    color: #fff !important;
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

// Segmented [earlier] [later] [Δ Change] switch with a sliding highlight
// Side by side: two half-width charts under a band of per-year headings
.chord-canvas-wrap--split {
  .chord-svg {
    inset: auto;
    top: 40px;
    left: 50%;
    width: 50%;
    height: calc(100% - 40px);
  }
  .chord-svg--base {
    left: 0;
    border-right: 1px dashed #dde2f0;
  }
}
.tf-split-head {
  position: absolute;
  top: 0;
  width: 50%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-family: 'GilroyMedium', system-ui, sans-serif;
  font-variant-numeric: tabular-nums;
  animation: tf-movers-in 0.3s ease-out;
  &--prev { left: 0; }
  &--curr { left: 50%; }
}
// Map flow thickness: a bottom-right corner button; clicking it slides the
// slider out to its left, growing from the button
.tf-arc-width {
  position: absolute;
  right: 10px;
  bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'GilroyMedium', system-ui, sans-serif;
}
.tf-arc-width__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid #e0e3ef;
  border-radius: 3px;
  background: rgba(255,255,255,0.95);
  color: #5b6378;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: #213CE5; color: #213CE5; }
  &.active { border-color: #213CE5; background: #213CE5; color: #fff; }
}
.tf-arc-width__panel {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #e0e3ef;
  border-radius: 3px;
  background: rgba(255,255,255,0.95);
  font-size: 12px;
  color: #5b6378;
  white-space: nowrap;
  transform-origin: right center;
  input[type='range'] {
    width: 140px;
    accent-color: #213CE5;
    cursor: pointer;
  }
}
.tf-arc-width__value {
  min-width: 34px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: #000;
}
.tf-arc-width-slide-enter-active,
.tf-arc-width-slide-leave-active {
  transition: transform 0.22s ease, opacity 0.18s ease;
}
.tf-arc-width-slide-enter-from,
.tf-arc-width-slide-leave-to {
  opacity: 0;
  transform: translateX(19px) scaleX(0.1);
}
.tf-split-head__year { font-size: 16px; font-weight: 700; color: #000; }
.tf-split-head__total { font-size: 12px; color: #5b6378; }

.tf-year-switch {
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  margin-left: auto;
  padding: 2px;
  border-radius: 6px;
  background: #e4e7f2;
  z-index: 11; // above the absolutely-centred filters/reset group
}
.tf-year-switch__glider {
  position: absolute;
  top: 2px; bottom: 2px; left: 2px;
  width: calc((100% - 4px) / 2);
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(20, 30, 60, 0.18);
  transition: transform 0.28s cubic-bezier(0.3, 0.7, 0.2, 1);
}
.tf-year-switch__btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-width: 62px;
  padding: 4px 10px;
  border: none !important;
  background: transparent !important;
  color: #5b6378;
  font-size: 12px;
  font-family: 'GilroyMedium', system-ui, sans-serif;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s;
  &:hover { color: #213CE5; }
  &.active { color: #213CE5; font-weight: 600; }
}
// Legend + headline total (exported with the chart)
.tf-change-legend {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px 24px;
  padding: 10px 20px 2px;
  font-size: 12px;
  font-family: 'GilroyMedium', system-ui, sans-serif;
  color: #000;
}
.tf-change-legend__scale {
  display: flex;
  align-items: center;
  gap: 12px;
}
.tf-change-legend__title { font-weight: 600; white-space: nowrap; }
.tf-change-legend__bar-wrap {
  width: 220px;
  padding-bottom: 14px;
  position: relative;
}
.tf-change-legend__bar {
  height: 8px;
  border-radius: 4px;
}
.tf-change-legend__ticks span {
  position: absolute;
  top: 11px;
  transform: translateX(-50%);
  font-size: 10px;
  color: #5b6378;
  white-space: nowrap;
}
.tf-change-legend__new {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #5b6378;
  white-space: nowrap;
  i { width: 10px; height: 10px; border-radius: 2px; display: inline-block; }
}
.tf-change-legend__total {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.tf-change-legend__muted { color: #5b6378; }
.tf-change-legend__arrow { color: #9dabaf; }
.tf-change-pill {
  padding: 2px 8px;
  border-radius: 10px;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}

// Biggest movers side panel
.tf-movers {
  flex: 0 0 270px;
  max-height: 560px;
  overflow-y: auto;
  padding: 12px 14px;
  border: 1px solid #e8ecff;
  background: #fafbff;
  font-family: 'GilroyMedium', system-ui, sans-serif;
  animation: tf-movers-in 0.3s ease-out;
}
@keyframes tf-movers-in {
  from { opacity: 0; transform: translateX(12px); }
  to   { opacity: 1; transform: none; }
}
.tf-movers__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 4px;
  .tf-panel-title { margin: 0; }
}
.tf-movers__years { font-size: 11px; color: #5b6378; font-variant-numeric: tabular-nums; }
.tf-movers__group {
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 12px 0 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.tf-movers__empty { margin: 0; font-size: 12px; color: #9dabaf; }
.tf-mover {
  padding: 6px 6px 5px;
  margin: 0 -6px;
  border-radius: 4px;
  cursor: default;
  transition: background 0.12s;
  &:hover { background: #eef0ff; }
}
.tf-mover__label {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: #000;
}
.tf-mover__route { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tf-mover__delta { flex-shrink: 0; font-weight: 600; font-variant-numeric: tabular-nums; }
.tf-mover__bar-track {
  height: 4px;
  margin: 4px 0 3px;
  border-radius: 2px;
  background: #e8ebf3;
  overflow: hidden;
}
.tf-mover__bar {
  height: 100%;
  border-radius: 2px;
  animation: tf-bar-grow 0.5s cubic-bezier(0.3, 0.7, 0.2, 1);
  transform-origin: left;
}
@keyframes tf-bar-grow {
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
}
.tf-mover__sub {
  display: flex;
  justify-content: space-between;
  font-size: 10.5px;
  color: #5b6378;
  font-variant-numeric: tabular-nums;
}
</style>
